// #40 Hours-Log Phase-1 — weekly work-task draft view.
//
// A PSU-style, output-only surface that renders the timesheet draft the
// agent compiles from the briefing/state (design: docs/plans/40-hours-log-
// design.md). Two sections:
//   §A — 4-week activity breakout: per week, the work items with agent-known
//        activity, grouped Category → Item, each with an attribution label.
//   §B — known-but-no-activity: open items quiet across the whole 4-week
//        window (the memory-jog for off-record work).
//
// Attribution names are SCALE TEAM ONLY (the people who log hours) — confirmed
// (the owner) renders plain, inferred renders "possibly <name>". External
// senders never appear. Phase 1 is read-only; hours input is Phase 2.
//
// Data source: GET /hours-log (session-authenticated; reads the snapshot the
// agent pushed via PUT /admin/hours-log/:slug on the last weekly run).

import {
  ApiError, downloadFile, getJson, postJson, putJson, wireDownloadControl,
} from "./api";
// G2 Phase B: reuse the briefing's association abbreviation (MA/NT/ST) + colour
// chip so the split rows read identically across the briefing and the Hours Log;
// and the SAME action_set_associations submission for inline tagging on the None
// row (confirmProposedAssociations — a no-op in fixture mode).
import { renderAssocTagChip } from "./briefings";
import { confirmProposedAssociations } from "./forms";
import { actionItemKey } from "./target_id";

export interface Attribution {
  confirmed: string[];
  possibly: string[];
}

export interface HoursLogAction {
  ai_slug: string | null;
  display_number: string | null;
  text: string | null;
  narrative: string | null;
  priority_status: string;
  // Per-action "worked by" (may be empty → defers to the Item owner).
  attribution?: Attribution;
  // G2: the Action's confirmed association tag-set (agent/hours_log.py:346). The
  // grouped split-entry view composes each entity's narrative from the Actions
  // scoped to it, and an untagged Action (empty) feeds the Unallocated sub-row.
  association_tags?: string[];
}

export interface HoursLogItem {
  slug: string;
  display_number: string | null;
  name: string;
  // #89: phase band (Phase → Category → Item), mirrors the briefing.
  phase?: string | null;
  thread_name: string | null;
  thread_display_number: string | null;
  narrative: string | null;
  is_new?: boolean;
  // #109b: the Item's purpose-built "worked-this-week" narrative (the synth's
  // Item.hours_log_narrative), "more verbal, less list form" — it replaces the
  // #89 semicolon roll-up of Action titles (kept as the fallback for older
  // snapshots). Same field name, so the render below is unchanged.
  // `actions`/`attribution` are retained for the #91 per-person hours track.
  actions_summary?: string;
  attribution: Attribution;
  actions: HoursLogAction[];
  // G2: the Item's billing-rollup axes (agent/hours_log.py). `association_tags`
  // is the UNION of its Actions' confirmed association slugs (0 = unallocated,
  // 1 = one entity, >1 = spans entities → the grouped view places it under each
  // and flags it). `sub_project` is the Category's sub_project (1:1). The flat
  // (Ungrouped) render ignores both; the grouped read-out groups by them.
  association_tags?: string[];
  sub_project?: string | null;
  // G2 Phase B: the Category slug — used to build the Action target_id for an
  // inline association tag fired from the Hours Log None row.
  thread_slug?: string | null;
}

export interface HoursLogWeek {
  week_start: string;
  week_end: string;
  items: HoursLogItem[];
}

export interface HoursLogNoActivityItem {
  slug: string;
  display_number: string | null;
  name: string;
  phase?: string | null;
  thread_name: string | null;
  thread_display_number: string | null;
  narrative: string | null;
  // #40c: real most-recent week with activity (week_end), derived from an
  // extended briefing lookback. null = dormant beyond the lookback. Prefer this
  // over `last_active`, which is week_end-stamped on every upsert (unreliable).
  last_touched?: string | null;
  last_active: string | null;
  attribution: Attribution;
}

export interface HoursLogSnapshot {
  project_slug: string;
  snapshot_at: string | null;
  weeks: HoursLogWeek[];
  no_activity: HoursLogNoActivityItem[];
  counts?: {
    weeks: number;
    active_items: number;
    no_activity_items: number;
  };
  // G2: the project configures a rollup axis (association = billing entity) →
  // the ungrouped/grouped toggle + the grouped (by-entity) export are available.
  grouped_available?: boolean;
  // G2 (Phase A): the ordered entity list (config order) + the entity label, so
  // the on-screen grouped read-out can render proper entity headers (the
  // per-item `association_tags` are slugs). Present only when grouped_available;
  // the SPA degrades to a prettified slug / "Association" if absent.
  associations?: { slug: string; name: string }[];
  entity_label?: string | null;
  // G2 Phase B: the latest run id — the SPA posts an inline association tag
  // (POST /feedback/<run_id>) under it. Absent → inline tagging is disabled.
  run_id?: string | null;
}

export async function fetchHoursLog(): Promise<HoursLogSnapshot> {
  return getJson<HoursLogSnapshot>("/hours-log");
}

// --- §7 Hours-Log Phase-2 — self-reported hours entry ------------------------
//
// A roster member (the signed-in recipient, joined to the project `team`
// server-side) enters their OWN hours per Item, plus employee-week misc
// buckets and add-a-row. Off-roster recipients (board members) see the
// read-only Phase-1 view unchanged. Design: 40-hours-log-design.md §7.

export interface MiscBuckets {
  office_admin: number;
  non_billable: number;
  pto: number;
}
const MISC_BUCKETS: (keyof MiscBuckets)[] = ["office_admin", "non_billable", "pto"];
const MISC_LABELS: Record<keyof MiscBuckets, string> = {
  office_admin: "Office / Admin",
  non_billable: "Non-billable",
  pto: "PTO",
};

// §7 #2 — the per-week section holding self-added work (custom "not listed"
// rows + logged open-items). One source of truth for the SPA; the Excel uses
// the same wording. Operator to finalize the wording (2026-07-23).
export const ALSO_WORKED_LABEL = "Also worked this week";

export interface HoursEntryRowInput {
  row_key: string;
  label: string;
  hours: number;
  note?: string | null;
  // HL-6c: RECEIVED from /me & /all (never sent) — the cell is HR-locked against
  // the owner, and who entered it (a resolved name, never an email). Gated to an
  // HR viewer or the affected employee; a third teammate gets locked:false, null.
  locked?: boolean;
  edited_by_name?: string | null;
}

// HL-6c: per-bucket misc lock/attribution, keyed by bucket (from /me & /all).
export type MiscLocks = Partial<
  Record<keyof MiscBuckets, { locked: boolean; edited_by_name: string | null }>
>;

export interface HoursEntriesMeWeek {
  week_end: string;
  rows: HoursEntryRowInput[];
  misc: MiscBuckets | null;
  updated_at: string | null;
  // HL-6c: per-bucket lock/attribution for the misc row (null = none/gated).
  misc_locks?: MiscLocks | null;
}

// HL-6d (S8): the billing months HR has frozen (period sign-off), keyed by
// "YYYY-MM". `locked_by_name` is the HR editor resolved to a display name (never
// an email). A month present here is read-only for EVERYONE until HR unfreezes.
export interface PeriodLock {
  locked: boolean;
  locked_by_name: string | null;
  locked_at: string;
}
export type LockedMonths = Record<string, PeriodLock>;

export interface HoursEntriesMe {
  can_enter: boolean;
  display_name: string | null;
  // HL-6b: true when the signed-in member is an HR admin (may edit teammate
  // columns). Optional so pre-HL-6b fixtures/tests type-check; the Worker always
  // sends it. Never trusted for authorization — the PUT re-checks server-side.
  can_edit_all?: boolean;
  weeks: HoursEntriesMeWeek[];
  // HL-6d: frozen billing months. Optional for pre-S8 fixtures/tests.
  locked_months?: LockedMonths;
}

export async function fetchHoursEntriesMe(): Promise<HoursEntriesMe> {
  return getJson<HoursEntriesMe>("/hours-entries/me");
}

// HL-6a — the all-staff grid payload (GET /hours-entries/all). Gated server-side:
// `can_view_all` is true ONLY for a roster member on an opted-in project
// (`hours_all_staff_visible`); otherwise `members` is []. Each member carries the
// same per-week shape as /me plus `is_self` (the one editable column). NO email —
// the roster's emails never reach the SPA (`name` identifies a column).
export interface HoursAllStaffMember {
  // HL-6b: the OPAQUE per-member handle (a hash of the email; never the email).
  // The SPA's only reference to a teammate when HR saves their column.
  id: string;
  name: string;
  is_self: boolean;
  weeks: HoursEntriesMeWeek[];
}
export interface HoursEntriesAll {
  can_view_all: boolean;
  // HL-6b: mirrors /me — true when THIS viewer may edit teammate columns.
  can_edit_all?: boolean;
  members: HoursAllStaffMember[];
  // HL-6d: frozen billing months (same shape + source as /me).
  locked_months?: LockedMonths;
}
export async function fetchHoursEntriesAll(): Promise<HoursEntriesAll> {
  return getJson<HoursEntriesAll>("/hours-entries/all");
}

// HL-6d (S8) — HR freezes (locked:true) or releases (locked:false) one billing
// month. HR-only (the Worker re-checks the roster admin flag). Returns the
// updated locked-month map for an in-session echo (no reload needed).
export async function saveHoursPeriodLock(
  month: string, locked: boolean,
): Promise<{ ok: boolean; locked_months: LockedMonths }> {
  return putJson<{ ok: boolean; locked_months: LockedMonths }>(
    "/hours-period-lock", { month, locked },
  );
}

// Column-header initials from a roster display name: first + last initial
// (single-token → first two letters), upper-cased. Collisions across the roster
// are disambiguated with a trailing digit so two "RF" read "RF" / "RF2"; the full
// name always rides the cell's title tooltip regardless.
export function memberInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}
export function rosterInitials(names: string[]): string[] {
  const base = names.map(memberInitials);
  const seen = new Map<string, number>();
  return base.map((ini) => {
    const n = (seen.get(ini) ?? 0) + 1;
    seen.set(ini, n);
    return n === 1 ? ini : `${ini}${n}`;
  });
}

export interface SaveHoursResult {
  ok: boolean;
  updated_at: string;
}

export async function saveHoursEntries(
  weekEnd: string, rows: HoursEntryRowInput[], misc: MiscBuckets,
  targetId?: string,
  unlock?: string[], unlockMisc?: (keyof MiscBuckets)[],
): Promise<SaveHoursResult> {
  // HL-6b: `target_id` (an opaque handle from /all) present → an HR edit-all
  // write to that teammate's column; absent → a normal self-save. The Worker
  // authorizes + resolves it server-side.
  // HL-6c: `unlock`/`unlock_misc` (HR only) return locked cells to the employee.
  return putJson<SaveHoursResult>("/hours-entries", {
    week_end: weekEnd, rows, misc,
    ...(targetId ? { target_id: targetId } : {}),
    ...(unlock && unlock.length ? { unlock } : {}),
    ...(unlockMisc && unlockMisc.length ? { unlock_misc: unlockMisc } : {}),
  });
}

// Round to the quarter-hour grid the Worker enforces (0.25 steps). A blank /
// non-numeric field reads as 0. Clamped to [0, 168].
export function normalizeHours(raw: string | number | null | undefined): number {
  const n = typeof raw === "number" ? raw : parseFloat(String(raw ?? "").trim());
  if (!isFinite(n) || n <= 0) return 0;
  return Math.min(168, Math.round(n * 4) / 4);
}

// One editable row's working value: the denormalized label travels with it so
// a later slug merge/close can't orphan the billing entry (§7b).
interface WorkingRow {
  label: string;
  hours: number;
}

// The per-week working copy the SPA mutates before Save. `item:` rows for the
// week's §A items and any §B items logged in place live here keyed by row_key;
// custom rows carry a `custom:` key.
export class WeekEntryModel {
  readonly weekEnd: string;
  private rows = new Map<string, WorkingRow>();
  misc: MiscBuckets = { office_admin: 0, non_billable: 0, pto: 0 };
  private savedSig: string;
  private customSeq = 0;
  // HL-6c: cells HR has locked (rowKey → editor display name), seeded from the
  // payload; and the ones HR has UNLOCKED this session (an intent sent on Save).
  private lockedRows = new Map<string, string | null>();
  private unlockedRows = new Set<string>();
  private miscLocks: MiscLocks = {};
  private miscUnlocked = new Set<keyof MiscBuckets>();
  // Hours at the last save/seed — the baseline the optimistic lock echo diffs
  // against to know which cells an HR edit actually changed.
  private baselineHours = new Map<string, number>();
  private baselineMisc: MiscBuckets = { office_admin: 0, non_billable: 0, pto: 0 };
  // HL-6c: set (to the HR name) on a TEAMMATE model — then every edit to a cell
  // locks it immediately under HR (auto-lock-on-edit), so the 🔒 shows live and
  // an edit re-asserts the lock over a pending unlock. null on a self model → an
  // owner editing their own cell never locks it.
  private hrEditor: string | null = null;

  constructor(seed?: HoursEntriesMeWeek) {
    this.weekEnd = seed?.week_end ?? "";
    for (const r of seed?.rows ?? []) {
      this.rows.set(r.row_key, {
        label: r.label, hours: normalizeHours(r.hours),
      });
      if (r.locked) this.lockedRows.set(r.row_key, r.edited_by_name ?? null);
    }
    if (seed?.misc) {
      for (const b of MISC_BUCKETS) this.misc[b] = normalizeHours(seed.misc[b]);
    }
    this.miscLocks = seed?.misc_locks ?? {};
    // Seed the custom-key counter past any seeded custom rows so a fresh
    // add-a-row can't collide with a key restored from /me (which would
    // silently overwrite an existing entry).
    const prefix = `custom:${this.weekEnd}-`;
    for (const key of this.rows.keys()) {
      if (key.startsWith(prefix)) {
        // A TAGGED custom key is `custom:<wk>-<n>:<assoc>` — seed off the seq <n>
        // (before any `:<assoc>`), else Number("<n>:<assoc>") is NaN and the
        // counter never advances past a tagged row, so a fresh add-a-row could
        // mint `custom:<wk>-<n>` colliding with the tagged row's base key.
        const n = Number(key.slice(prefix.length).split(":")[0]);
        if (Number.isInteger(n) && n >= this.customSeq) this.customSeq = n + 1;
      }
    }
    for (const [k, r] of this.rows) this.baselineHours.set(k, r.hours);
    this.baselineMisc = { ...this.misc };
    this.savedSig = this.signature();
  }

  getHours(rowKey: string): number {
    return this.rows.get(rowKey)?.hours ?? 0;
  }

  // Whether a row is PRESENT (entered) at all — the tri-state distinction an
  // explicit "0" (addressed, no time) needs: present-0 vs absent both read 0
  // hours, but only the present one renders/persists. See clearHours.
  hasEntry(rowKey: string): boolean {
    return this.rows.has(rowKey);
  }

  // HL-6c: this is a teammate (HR) model — every edit auto-locks under `name`.
  setHrEditor(name: string | null): void {
    this.hrEditor = name;
  }

  // Set a row to an explicit hours value. An explicit 0 ("addressed, no time")
  // is a REAL, persisted value and is KEPT — only clearHours (a blanked field)
  // removes a row. normalizeHours floors negatives to 0, so h is always >= 0.
  // HL-6c: on an HR model, an edit locks the cell now (and cancels a pending
  // unlock — editing re-asserts HR control).
  setHours(rowKey: string, label: string, hours: number): void {
    this.rows.set(rowKey, { label, hours: normalizeHours(hours) });
    this.unlockedRows.delete(rowKey);
    if (this.hrEditor) this.lockedRows.set(rowKey, this.hrEditor);
  }

  // Remove a row entirely — the field was cleared/blanked (unentered), which is
  // distinct from an explicit 0. A blank never persists; an explicit 0 does.
  clearHours(rowKey: string): void {
    this.rows.delete(rowKey);
    this.lockedRows.delete(rowKey);
    this.unlockedRows.delete(rowKey);
  }

  setMisc(bucket: keyof MiscBuckets, hours: number): void {
    this.misc[bucket] = normalizeHours(hours);
    this.miscUnlocked.delete(bucket);
    if (this.hrEditor) {
      this.miscLocks[bucket] = { locked: true, edited_by_name: this.hrEditor };
    }
  }

  // Rename a still-present row (an editable custom / add-a-row label). No-op if
  // the row was dropped (hours went to 0) — a label with no hours never saves.
  setLabel(rowKey: string, label: string): void {
    const r = this.rows.get(rowKey);
    if (r) r.label = label;
  }

  // A custom add-a-row starts empty; the row_key is minted client-side and
  // is stable for this session so repeated edits target the same entry.
  addCustomRow(label: string, hours: number): string {
    const key = `custom:${this.weekEnd}-${this.customSeq++}`;
    this.setHours(key, label, hours);
    return key;
  }

  // The committed custom (add-a-row) entries — re-rendered on every repaint /
  // reload so they survive a filter change (they persist in the model, but the
  // add-a-row host is rebuilt each render). Order is insertion order.
  customRows(): Array<{ rowKey: string; label: string }> {
    const out: Array<{ rowKey: string; label: string }> = [];
    for (const [rowKey, r] of this.rows) {
      if (rowKey.startsWith("custom:")) out.push({ rowKey, label: r.label });
    }
    return out;
  }

  total(): number {
    let t = 0;
    for (const r of this.rows.values()) t += r.hours;
    for (const b of MISC_BUCKETS) t += this.misc[b];
    return Math.round(t * 4) / 4;
  }

  // HL-5: the billable week total — project rows only, EXCLUDING misc/PTO (the
  // billing view drops non-project time). Used for the billing week total.
  billableTotal(): number {
    let t = 0;
    for (const r of this.rows.values()) t += r.hours;
    return Math.round(t * 4) / 4;
  }

  // The PUT payload: every PRESENT row, including an explicit 0 ("addressed,
  // no time"). A blanked/unentered row is absent from the map (clearHours) and
  // so never serialized. Sorted for a stable signature/diff.
  serializeRows(): HoursEntryRowInput[] {
    return [...this.rows.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([row_key, r]) => ({ row_key, label: r.label, hours: r.hours }));
  }

  private signature(): string {
    return JSON.stringify([this.serializeRows(), this.misc]);
  }

  isDirty(): boolean {
    // A pending unlock is a real change even when no hours moved.
    return this.signature() !== this.savedSig
      || this.unlockedRows.size > 0 || this.miscUnlocked.size > 0;
  }

  // `lockChangedAs` (the HR editor's name) is passed on an HR TEAMMATE save: the
  // cells HR just CHANGED become locked immediately (the optimistic echo of the
  // server's lock-on-edit), so the 🔒 shows on Save instead of only after a
  // reload. A self-save passes nothing → the owner's own cells never lock.
  markSaved(lockChangedAs?: string): void {
    if (lockChangedAs) {
      for (const k of this.changedSinceSave()) {
        // A cell being unlocked this save stays unlocked (matches the Worker's
        // unlock-wins merge); otherwise a changed cell locks under the HR editor.
        if (this.rows.has(k) && !this.unlockedRows.has(k)) {
          this.lockedRows.set(k, lockChangedAs);
        }
      }
      for (const b of MISC_BUCKETS) {
        if (this.misc[b] !== this.baselineMisc[b] && !this.miscUnlocked.has(b)) {
          this.miscLocks[b] = { locked: true, edited_by_name: lockChangedAs };
        }
      }
    }
    // Applied unlocks are now persisted → the cell is genuinely unlocked.
    for (const key of this.unlockedRows) this.lockedRows.delete(key);
    for (const b of this.miscUnlocked) delete this.miscLocks[b];
    this.unlockedRows.clear();
    this.miscUnlocked.clear();
    // The saved values become the new baseline for the next edit diff.
    this.baselineHours = new Map(
      [...this.rows.entries()].map(([k, r]) => [k, r.hours]),
    );
    this.baselineMisc = { ...this.misc };
    this.savedSig = this.signature();
  }

  // Row keys whose hours differ from the last save/seed (changed, added, or
  // removed) — the set an HR save locks.
  private changedSinceSave(): string[] {
    const out: string[] = [];
    const keys = new Set([...this.rows.keys(), ...this.baselineHours.keys()]);
    for (const k of keys) {
      if (this.rows.get(k)?.hours !== this.baselineHours.get(k)) out.push(k);
    }
    return out;
  }

  // --- HL-6c per-cell lock -------------------------------------------------
  // A cell is locked (HR-entered, owner read-only) unless HR unlocked it this
  // session. `lockEditor` is the HR name for the tooltip.
  isLocked(rowKey: string): boolean {
    return this.lockedRows.has(rowKey) && !this.unlockedRows.has(rowKey);
  }
  lockEditor(rowKey: string): string | null {
    return this.lockedRows.get(rowKey) ?? null;
  }
  unlockRow(rowKey: string): void {
    if (this.lockedRows.has(rowKey)) this.unlockedRows.add(rowKey);
  }
  unlockedRowKeys(): string[] {
    return [...this.unlockedRows];
  }
  isMiscLocked(bucket: keyof MiscBuckets): boolean {
    return this.miscLocks[bucket]?.locked === true && !this.miscUnlocked.has(bucket);
  }
  miscEditor(bucket: keyof MiscBuckets): string | null {
    return this.miscLocks[bucket]?.edited_by_name ?? null;
  }
  unlockMiscBucket(bucket: keyof MiscBuckets): void {
    if (this.miscLocks[bucket]?.locked) this.miscUnlocked.add(bucket);
  }
  unlockedMiscBuckets(): (keyof MiscBuckets)[] {
    return [...this.miscUnlocked];
  }
}

// Ties the per-week models to the snapshot + save transport. §B (no-activity)
// rows have no week of their own, so they attach to the MOST-RECENT displayed
// week's document (§7d + operator 2026-07-23) — one Save flushes both.
export class HoursEntryController {
  readonly canEnter: boolean;
  readonly displayName: string | null;
  // HL-6b: this viewer is an HR admin who may edit teammate columns.
  readonly canEditAll: boolean;
  private byWeek = new Map<string, WeekEntryModel>();
  private order: string[] = [];
  private saveFn: typeof saveHoursEntries;
  // HL-6b: teammate working copies HR edits — memberId (opaque handle) → weekEnd
  // → model — plus the seed data (/all member weeks) they lazily instantiate from.
  private byMemberWeek = new Map<string, Map<string, WeekEntryModel>>();
  private memberWeekSeed = new Map<string, Map<string, HoursEntriesMeWeek>>();
  // HL-6b (B3 echo): member-weeks THIS HR viewer saved this session, keyed
  // `${memberId}|${weekEnd}` → {name, at}. The server only reports `edited_by`
  // on the NEXT /all fetch, so this optimistically surfaces the ✎ marker
  // immediately after a save (header-level; per-cell attribution is S7).
  private hrEdited = new Map<string, { name: string; at: string }>();
  // HL-6d: the billing months HR has frozen (period sign-off). Same doc from
  // /me + /all; used to render frozen cells read-only + drive the HR freeze
  // toggle. Mutated in-session by a freeze/unfreeze so the echo is immediate.
  lockedMonths: LockedMonths;

  constructor(
    snapshot: HoursLogSnapshot,
    me: HoursEntriesMe | null,
    saveFn: typeof saveHoursEntries = saveHoursEntries,
    allStaff?: HoursEntriesAll | null,
  ) {
    this.canEnter = Boolean(me?.can_enter);
    this.displayName = me?.display_name ?? null;
    this.canEditAll = Boolean(me?.can_edit_all);
    this.lockedMonths = me?.locked_months ?? allStaff?.locked_months ?? {};
    this.saveFn = saveFn;
    const seedByWeek = new Map<string, HoursEntriesMeWeek>();
    for (const w of me?.weeks ?? []) seedByWeek.set(w.week_end, w);
    for (const w of snapshot.weeks) {
      this.order.push(w.week_end);
      // Always seed the model with its week_end, even when /me has no entry data
      // for that week — otherwise the model's weekEnd defaults to "" and anything
      // keyed on it (HL-6d billing_month freeze check; the custom-row key prefix)
      // silently misfires for an unlogged week.
      this.byWeek.set(
        w.week_end,
        new WeekEntryModel(
          seedByWeek.get(w.week_end)
            ?? { week_end: w.week_end, rows: [], misc: null, updated_at: null },
        ),
      );
    }
    // HL-6b: capture the seed for each editable teammate (non-self member) so
    // memberModel() can instantiate its working copy on first edit.
    if (this.canEditAll && allStaff) {
      for (const m of allStaff.members) {
        if (m.is_self) continue;
        const byWk = new Map<string, HoursEntriesMeWeek>();
        for (const w of m.weeks) byWk.set(w.week_end, w);
        this.memberWeekSeed.set(m.id, byWk);
      }
    }
  }

  week(weekEnd: string): WeekEntryModel | undefined {
    return this.byWeek.get(weekEnd);
  }

  // HL-6d: replace the frozen-month map after a freeze/unfreeze (the route
  // echoes the updated set) so the in-session render reflects it without a fetch.
  // Defensive default: a malformed/empty echo must not null the map (isWeekFrozen
  // indexes into it every render).
  setLockedMonths(next: LockedMonths | null | undefined): void {
    this.lockedMonths = next ?? {};
  }
  // A week is frozen when its billing month has been signed off. Drives the
  // read-only cell render; the Worker PUT is the authoritative gate regardless.
  isWeekFrozen(weekEnd: string): boolean {
    return this.lockedMonths[billingMonth(weekEnd)]?.locked === true;
  }

  // HL-6b: the working copy for a TEAMMATE's column-week (HR edit-all). Returns
  // undefined unless this viewer is HR and `memberId` is a known editable
  // teammate. Lazily seeded from that member's /all week (empty if unlogged).
  memberModel(memberId: string, weekEnd: string): WeekEntryModel | undefined {
    if (!this.canEditAll) return undefined;
    const seedByWk = this.memberWeekSeed.get(memberId);
    if (!seedByWk) return undefined;
    let byWk = this.byMemberWeek.get(memberId);
    if (!byWk) {
      byWk = new Map();
      this.byMemberWeek.set(memberId, byWk);
    }
    let model = byWk.get(weekEnd);
    if (!model) {
      const seedWeek: HoursEntriesMeWeek = seedByWk.get(weekEnd) ?? {
        week_end: weekEnd, rows: [], misc: null, updated_at: null,
      };
      model = new WeekEntryModel(seedWeek);
      // HL-6c: a teammate model is HR-edited → every edit auto-locks the cell.
      model.setHrEditor(this.displayName ?? "HR");
      byWk.set(weekEnd, model);
    }
    return model;
  }

  // HL-6b: an EXISTING teammate working copy, WITHOUT instantiating one. For
  // read-only consumers (the billing preview) that should reflect a live HR edit
  // where one has been made, but must not create models just by reading.
  peekMemberModel(memberId: string, weekEnd: string): WeekEntryModel | undefined {
    return this.byMemberWeek.get(memberId)?.get(weekEnd);
  }

  // HL-6b (grid "Also worked"): a fresh custom row_key SHARED across every member
  // for a week, so an ad-hoc row is ONE line with per-person hours (like an item
  // row), not a per-member row. Scans existing keys so it never collides; the
  // `-s<n>` suffix keeps it distinct from the card view's per-model `-<n>` keys.
  private sharedCustomSeq = new Map<string, number>();
  mintSharedCustomKey(weekEnd: string): string {
    const used = new Set<string>();
    const collect = (m?: WeekEntryModel): void => {
      // Collide on the BASE key — a TAGGED custom (custom:<week>-s<n>:<assoc>) must
      // block re-minting its base custom:<week>-s<n>, else a fresh add-a-row could
      // mint a key that a later re-tag collapses onto, silently overwriting it.
      if (m) for (const { rowKey } of m.customRows()) used.add(customRowBase(rowKey));
    };
    collect(this.byWeek.get(weekEnd));
    for (const byWk of this.byMemberWeek.values()) collect(byWk.get(weekEnd));
    let n = this.sharedCustomSeq.get(weekEnd) ?? 0;
    let key = `custom:${weekEnd}-s${n}`;
    while (used.has(key)) { n += 1; key = `custom:${weekEnd}-s${n}`; }
    this.sharedCustomSeq.set(weekEnd, n + 1);
    return key;
  }

  // HL-6b: is anything dirty for this week — the self column OR any teammate
  // column HR has edited? Drives the shared Save bar's enabled/dirty state.
  weekDirty(weekEnd: string): boolean {
    if (this.byWeek.get(weekEnd)?.isDirty()) return true;
    for (const byWk of this.byMemberWeek.values()) {
      if (byWk.get(weekEnd)?.isDirty()) return true;
    }
    return false;
  }

  // The document §B rows flush into (weeks are snapshot-ordered, newest first).
  mostRecentWeek(): string | null {
    return this.order[0] ?? null;
  }

  async save(weekEnd: string): Promise<void> {
    // Self column: save only when dirty (a clean re-save is a wasteful no-op).
    const self = this.byWeek.get(weekEnd);
    if (self && self.isDirty()) {
      await this.saveFn(weekEnd, self.serializeRows(), self.misc);
      self.markSaved();
    }
    // HL-6b: flush every teammate column HR edited for this week — each PUT
    // carries that member's opaque target_id (the Worker authorizes + resolves).
    for (const [memberId, byWk] of this.byMemberWeek) {
      const m = byWk.get(weekEnd);
      if (m && m.isDirty()) {
        // HL-6c: carry HR's unlock intents so the Worker returns those cells.
        await this.saveFn(
          weekEnd, m.serializeRows(), m.misc, memberId,
          m.unlockedRowKeys(), m.unlockedMiscBuckets(),
        );
        // Optimistic lock echo: the cells HR just changed lock under the HR name.
        m.markSaved(this.displayName ?? "HR");
        // B3 echo: this HR viewer just entered this teammate's week → surface
        // the ✎ now (the server will confirm it on the next /all fetch).
        this.hrEdited.set(`${memberId}|${weekEnd}`, {
          name: this.displayName ?? "HR", at: new Date().toISOString(),
        });
      }
    }
  }

  // HL-6b (B3 echo): the in-session HR-entered mark for a teammate's week, or
  // null. The grid ORs this with the server's `edited_by_name` so the ✎ shows
  // right after an HR save, not only after a reload.
  hrEditedMark(memberId: string, weekEnd: string): { name: string; at: string } | null {
    return this.hrEdited.get(`${memberId}|${weekEnd}`) ?? null;
  }

  // §B rows can target any displayed week, so the §B Save flushes EVERY dirty
  // week (self + any HR-edited teammate column). Returns the count of weeks saved.
  async saveAllDirty(): Promise<number> {
    const weeks = new Set<string>();
    for (const [wk, m] of this.byWeek) if (m.isDirty()) weeks.add(wk);
    for (const byWk of this.byMemberWeek.values()) {
      for (const [wk, m] of byWk) if (m.isDirty()) weeks.add(wk);
    }
    for (const wk of weeks) await this.save(wk);
    return weeks.size;
  }

  anyDirty(): boolean {
    for (const m of this.byWeek.values()) if (m.isDirty()) return true;
    for (const byWk of this.byMemberWeek.values()) {
      for (const m of byWk.values()) if (m.isDirty()) return true;
    }
    return false;
  }

  // §B placement (operator 2026-07-23): a no-activity item with hours logged in
  // a week surfaces under THAT week's card — and, because the same item can be
  // logged across several weeks, it appears under EVERY week it has hours in
  // (not just the first). It also stays in the bottom §B list regardless, so a
  // recipient can add it to another week. Returns week_end → items with hours
  // there.
  quietItemsByWeek(
    weekEnds: string[], items: HoursLogNoActivityItem[],
  ): Map<string, HoursLogNoActivityItem[]> {
    const byWeek = new Map<string, HoursLogNoActivityItem[]>();
    for (const we of weekEnds) {
      const here = items.filter(
        (it) => this.byWeek.get(we)?.hasEntry(`item:${it.slug}`) ?? false,
      );
      if (here.length) byWeek.set(we, here);
    }
    return byWeek;
  }
}

// --- Generate-and-archive (PSU-style) ---------------------------------------

export interface HoursLogGenerationListItem {
  id: string;
  generated_at: string;
  generated_by: string;
  filename: string;
  label: string | null | undefined;
}

export interface HoursLogGenerationsResponse {
  generations: HoursLogGenerationListItem[];
}

export async function fetchHoursLogGenerations(): Promise<HoursLogGenerationsResponse> {
  return getJson<HoursLogGenerationsResponse>("/hours-log-generations");
}

export interface HoursLogGenerationCreateResponse {
  id: string;
  generated_at: string;
  filename: string;
  label: string | null | undefined;
}

// HL-1 (client 2026-09-25): the DATE RANGE (period) is orthogonal to the VIEW
// (internal vs client-billing). `WeekScope` is the period only; `HoursView` is
// the toggle. A month range carries its concrete key (`month:2026-06`) so the
// picker can offer every billing month in the snapshot, not just current/last.
export type WeekScope = "all" | "recent" | `month:${string}`;
export type HoursView = "internal" | "billing";

const _MONTHS = ["January", "February", "March", "April", "May", "June", "July",
  "August", "September", "October", "November", "December"];
// "YYYY-MM" month key from an ISO date (UTC, avoids TZ drift on a bare date).
function monthKeyOf(iso: string): string {
  return (iso || "").slice(0, 7);
}

// HL-1: the calendar month a reporting week BILLS to — the month of its Thursday
// (majority-of-days rule). Mirrors agent `hours_log_xlsx.billing_month` EXACTLY so
// the on-screen month filter and the generated file agree on boundary weeks (e.g.
// week ending 8/2 → July on both). `weekEnd` is the Sunday; Thursday = -3 days.
export function billingMonth(weekEnd: string): string {
  const iso = (weekEnd || "").slice(0, 10);
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return "";
  d.setUTCDate(d.getUTCDate() - 3);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

function prevMonthKey(key: string): string {
  const [y, m] = key.split("-").map(Number);
  if (!y || !m) return key;
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, "0")}`;
}
function monthName(key: string): string {
  const m = Number(key.split("-")[1]);
  return (m >= 1 && m <= 12 ? _MONTHS[m - 1] : undefined) ?? key;
}

// The concrete billing month a range targets, or null for the rolling ranges.
function scopeMonthKey(s: WeekScope): string | null {
  return s.startsWith("month:") ? s.slice(6) : null;
}

// How many recent billing months the picker offers — matches the agent's
// prebake window (`_HOURS_LOG_PREBAKE_MONTHS`), so every offered month has a
// prebaked variant to download.
const _PICKER_MONTHS = 4;

// The distinct billing months present in the snapshot, newest first, capped —
// e.g. ["2026-09","2026-08",…]. Data-driven, so a longer window offers more.
export function snapshotMonths(
  snapshot: HoursLogSnapshot, cap = _PICKER_MONTHS,
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const w of snapshot.weeks) {
    const m = billingMonth(w.week_end);
    if (m && !seen.has(m)) { seen.add(m); out.push(m); }
  }
  return out.slice(0, cap);
}

// "2026-06" -> "June 2026".
function monthLabel(key: string): string {
  return `${monthName(key)} ${key.split("-")[0]}`;
}

export interface HoursLogGenerateRequest {
  scope: "all" | "recent" | "month";
  month?: string;
  billing?: boolean;
  // G2: the grouped-by-entity export (orthogonal to billing). Grouped variants
  // are month-scoped, so a grouped request always resolves to a month.
  grouped?: boolean;
  label?: string | null;
}

// Resolve the on-screen (date range × view) into the Worker generate request.
// `cur`/`last` derive from the latest week via `billingMonth`, so screen ==
// export. The billing view is always month-scoped: a rolling range falls back to
// the current month (the UI restricts billing to month ranges, so this is a
// belt — never the normal path).
export function hoursLogGenerateRequest(
  snapshot: HoursLogSnapshot, scope: WeekScope, view: HoursView = "internal",
  label: string | null = null, grouped = false,
): HoursLogGenerateRequest {
  const billing = view === "billing";
  // Billing AND grouped are month-scoped: a rolling range falls back to the most
  // recent month (grouped variants only exist per billing month).
  const m = scopeMonthKey(scope)
    ?? (billing || grouped ? snapshotMonths(snapshot)[0] : null);
  if (m) return { scope: "month", month: m, billing, grouped, label };
  return { scope: scope === "recent" ? "recent" : "all", grouped, label };
}

export async function createHoursLogGeneration(
  req: HoursLogGenerateRequest,
): Promise<HoursLogGenerationCreateResponse> {
  // Filename is left to the Worker (it picks the agent-computed name for the
  // variant); we send the resolved scope/month/billing + an optional label.
  return postJson<HoursLogGenerationCreateResponse>("/hours-log-generations", {
    scope: req.scope, month: req.month, billing: req.billing,
    grouped: req.grouped ?? false,
    label: req.label ?? null,
  });
}

// A 409 stale-regenerating means the Worker fired a re-render (§7 freshness):
// the consolidated .xlsx was stale relative to just-saved hours. The agent
// workflow re-renders + re-pushes in ~30-60s, so we poll Generate until it
// succeeds (or the budget runs out — then the caller surfaces a "try again"
// message; a later Generate will pick up the fresh doc).
function isStaleRegenerating(e: unknown): boolean {
  return e instanceof ApiError && e.status === 409
    && typeof e.body === "object" && e.body !== null
    && (e.body as { error?: string }).error === "stale-regenerating";
}

export interface GenerateOptions {
  retries?: number;
  delayMs?: number;
  onRegenerating?: () => void;
  sleep?: (ms: number) => Promise<void>;  // injectable for tests
  generate?: (
    req: HoursLogGenerateRequest,
  ) => Promise<HoursLogGenerationCreateResponse>;  // injectable for tests
}

export async function generateHoursLogWithRefresh(
  req: HoursLogGenerateRequest, opts: GenerateOptions = {},
): Promise<HoursLogGenerationCreateResponse> {
  // The regen is a GitHub Actions round-trip (~1-2 min incl. cold start). Poll
  // every 3s for up to ~2.5 min so a single Generate click waits it out and
  // returns the moment the fresh doc is ready — rather than giving up early and
  // asking the recipient to click again. (Option 3, operator 2026-07-23.)
  const retries = opts.retries ?? 50;
  const delayMs = opts.delayMs ?? 3000;
  const sleep = opts.sleep ?? ((ms: number) => new Promise((r) => setTimeout(r, ms)));
  const generate = opts.generate ?? createHoursLogGeneration;
  let announced = false;
  for (let attempt = 0; ; attempt++) {
    try {
      return await generate(req);
    } catch (e) {
      if (isStaleRegenerating(e) && attempt < retries) {
        if (!announced) { opts.onRegenerating?.(); announced = true; }
        await sleep(delayMs);
        continue;
      }
      throw e;
    }
  }
}

export function hoursLogGenerationDownloadUrl(id: string): string {
  return `/hours-log-generations/${encodeURIComponent(id)}/download`;
}

function formatGeneratedAt(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(iso || "");
  if (!m) return iso || "";
  return `${isoToShort(`${m[1]}-${m[2]}-${m[3]}`)} ${m[1]} ${m[4]}:${m[5]}`;
}

// Render the Hours-Log left rail: Generate button at the top, then the
// Generated Logs archive list (PSU pattern). `onGenerated` refreshes the view.
export function renderHoursLogRail(
  rail: HTMLElement,
  generations: HoursLogGenerationListItem[],
  getRequest: () => HoursLogGenerateRequest,
  onGenerated: () => void | Promise<void>,
): void {
  rail.innerHTML = "";

  const genCard = document.createElement("aside");
  genCard.className = "sidebar hours-log-generate-card";
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "hours-log-generate-btn";
  btn.textContent = "Generate Hours Log";
  // Explanation on hover rather than inline, to keep the rail clean.
  btn.title = "Generates the on-screen action list as an Excel timesheet (matches the week filter).";
  const err = document.createElement("p");
  err.className = "hours-log-generate-error";
  err.hidden = true;
  // Shown only when a save-triggered regen is in flight (the doc was stale) —
  // the recipient is told the wait is expected and bounded (operator 2026-07-23).
  const waitNote = document.createElement("p");
  waitNote.className = "hours-log-generate-note";
  waitNote.hidden = true;
  waitNote.textContent =
    "Compiling your latest hours into the file — this can take up to 2 minutes. "
    + "It'll download on its own when it's ready.";
  btn.addEventListener("click", async () => {
    btn.disabled = true;
    btn.textContent = "Generating…";
    err.hidden = true;
    waitNote.hidden = true;
    try {
      // Honor the on-screen week filter at generate time. §7 freshness: if the
      // consolidated doc is stale relative to just-saved hours, the Worker 409s
      // + re-renders; generateHoursLogWithRefresh polls until it's fresh.
      const result = await generateHoursLogWithRefresh(getRequest(), {
        onRegenerating: () => {
          btn.textContent = "Refreshing latest hours…";
          waitNote.hidden = false;  // regen in flight → explain the wait
        },
      });
      // Auto-download the freshly archived .xlsx via the authenticated fetch
      // layer (downloadFile) — a bare anchor can't send X-Project-Slug and 400s
      // post-#94 (see api.ts). Decoupled from onGenerated() so a download error
      // doesn't hide the archived row or read as "Generate failed" (the row's
      // Download link is the fallback). Mirrors the PSU Generate flow.
      waitNote.hidden = true;  // ready → the file downloads now
      try {
        await downloadFile(
          hoursLogGenerationDownloadUrl(result.id), result.filename,
        );
      } catch {
        // Generation succeeded; only the auto-download failed — non-fatal.
      }
      await onGenerated();
    } catch (e) {
      btn.disabled = false;
      btn.textContent = "Generate Hours Log";
      waitNote.hidden = true;
      err.hidden = false;
      if (e instanceof ApiError && e.status === 409) {
        // The re-render was still running when the retry budget ran out — the
        // dispatch is in flight, so a Generate in ~a minute will be fresh.
        err.textContent =
          "Still refreshing the latest hours — try Generate again in a moment.";
      } else if (e instanceof Error && /404/.test(e.message)) {
        err.textContent = "No timesheet rendered yet — it's refreshed each weekly run.";
      } else {
        err.textContent = `Generate failed: ${e instanceof Error ? e.message : String(e)}`;
      }
    }
  });
  genCard.appendChild(btn);
  genCard.appendChild(waitNote);
  genCard.appendChild(err);
  rail.appendChild(genCard);

  if (generations.length > 0) {
    const listCard = document.createElement("aside");
    listCard.className = "sidebar hours-log-generations-sidebar";
    const h = document.createElement("h2");
    h.textContent = `Generated Logs (${generations.length})`;
    listCard.appendChild(h);
    const list = document.createElement("ul");
    list.className = "kil-generations-list";
    for (const gen of generations) {
      const li = document.createElement("li");
      li.className = "kil-generation-row";
      const fname = document.createElement("span");
      fname.className = "kil-generation-filename";
      fname.textContent = gen.filename;
      fname.title = `Generated ${formatGeneratedAt(gen.generated_at)} by ${gen.generated_by}`;
      li.appendChild(fname);
      const dl = document.createElement("a");
      dl.className = "kil-generation-download";
      // Download through the authenticated fetch layer, NOT a bare href — a
      // bare-anchor GET can't send X-Project-Slug and 400s post-#94 (api.ts).
      dl.href = "#";
      dl.setAttribute("role", "button");
      dl.title = `Download ${gen.filename}`;
      wireDownloadControl(
        dl, hoursLogGenerationDownloadUrl(gen.id), gen.filename, (msg) => {
          let errEl = li.querySelector<HTMLElement>(".hours-log-download-error");
          if (!errEl) {
            errEl = document.createElement("div");
            errEl.className = "hours-log-download-error hours-log-generate-error";
            errEl.hidden = false;
            li.appendChild(errEl);
          }
          errEl.textContent = msg;
        });
      dl.innerHTML =
        '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" ' +
        'stroke="currentColor" stroke-width="2" stroke-linecap="round" ' +
        'stroke-linejoin="round" aria-hidden="true">' +
        '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>' +
        '<polyline points="7 10 12 15 17 10"/>' +
        '<line x1="12" y1="15" x2="12" y2="3"/></svg>';
      li.appendChild(dl);
      list.appendChild(li);
    }
    listCard.appendChild(list);
    rail.appendChild(listCard);
  }
}

// "Ray, Marcus, possibly Andrea" — confirmed first (plain), possibly last
// (each prefixed). Empty → "" (caller renders a dash).
export function formatAttribution(attr: Attribution | null | undefined): string {
  if (!attr) return "";
  const parts = [...(attr.confirmed ?? [])];
  for (const n of attr.possibly ?? []) parts.push(`possibly ${n}`);
  return parts.join(", ");
}

function fmtWeekRange(start: string, end: string): string {
  // "Jun 15 – Jun 21, 2026" — compact, locale-stable.
  const s = isoToShort(start);
  const e = isoToShort(end);
  const yr = (end || "").slice(0, 4);
  return s && e ? `${s} – ${e}${yr ? `, ${yr}` : ""}` : end;
}

function isoToShort(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  if (!m) return iso || "";
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const mo = months[Number(m[2]) - 1] ?? m[2];
  return `${mo} ${Number(m[3])}`;
}

// --- Renderer ---------------------------------------------------------------

// Entry-mode UI context threaded through the renderers. `refreshAll` repaints
// every registered control's live total + dirty/Save state after any edit
// (N ≤ 4 weeks, so a blanket refresh is cheap). §A and §B both re-render
// together on a filter change, so `refreshers` is fully volatile — reset at
// the top of each repaint before the sections re-register.
interface EntryUI {
  controller: HoursEntryController;
  refreshers: Array<() => void>;
  refreshAll: () => void;
  // Full re-render of §A + §B — used when a §B item's placement changes (it
  // gains/loses hours and must hop between the no-activity list and its week).
  repaint: () => void;
  // HL-6d: the frozen billing months (period sign-off). A cell whose week's
  // `billingMonth` is in this set is read-only for EVERYONE (incl. HR) — the
  // set is refreshed from the controller at the top of each repaint so a
  // freeze/unfreeze re-renders the cells without a page reload.
  frozenMonths: Set<string>;
}

export function renderHoursLog(
  root: HTMLElement,
  snapshot: HoursLogSnapshot,
  scopeRef: { value: WeekScope } = { value: "all" },
  controller: HoursEntryController | null = null,
  viewRef: { value: HoursView } = { value: "internal" },
  allStaff: HoursEntriesAll | null = null,
  // HL-6d: the freeze transport — injected so fixture mode can stub it (the
  // real POST has no backend there), mirroring the controller's saveFn.
  freezeFn: typeof saveHoursPeriodLock = saveHoursPeriodLock,
  // G2: the ungrouped/grouped export toggle (orthogonal to Internal/Billing).
  groupedRef: { value: boolean } = { value: false },
): void {
  root.innerHTML = "";
  const main = document.createElement("main");
  main.className = "hours-log-view";

  // HL-6a: the all-staff grid activates only when the server opted this viewer
  // in (can_view_all — a roster member on a `hours_all_staff_visible` project).
  // Column initials are computed once here so they stay stable across weeks.
  const gridCtx: GridCtx | null =
    allStaff?.can_view_all && allStaff.members.length > 0
      ? {
          members: allStaff.members,
          initials: rosterInitials(allStaff.members.map((m) => m.name)),
        }
      : null;

  // Entry mode is on only for a roster member (server-verified via /me).
  // Off-roster recipients get the Phase-1 read-only view unchanged.
  // G3 (2026-10-16): an admin-only overseer has canEnter=false (no hours of their
  // own) but canEditAll=true — they still need the EDITABLE all-staff grid, so
  // build the entry UI for EITHER. The self-entry message is gated on canEnter
  // below; the overseer is not a gridCtx column, so no self-cell renders for them.
  const ui: EntryUI | null = (controller?.canEnter || controller?.canEditAll)
    ? {
        controller, refreshers: [], refreshAll: () => {}, repaint: () => {},
        frozenMonths: new Set(Object.keys(controller.lockedMonths)),
      }
    : null;
  if (ui) ui.refreshAll = () => { for (const fn of ui.refreshers) fn(); };

  // (b) Header row — title left, the "Show" filter right-justified.
  const header = document.createElement("div");
  header.className = "hours-log-header";
  const heading = document.createElement("h2");
  heading.textContent = "Hours Log";
  header.appendChild(heading);
  main.appendChild(header);

  const subtitle = document.createElement("p");
  subtitle.className = "hours-log-subtitle";
  if (ui && ui.controller.canEnter) {
    const who = ui.controller.displayName ? ` as ${ui.controller.displayName}` : "";
    subtitle.textContent =
      `Enter your hours${who} on what you worked, then Save each week. ` +
      "Leave a cell blank if you didn't work on it; enter 0 to log it as " +
      "addressed with no time." +
      // HL-6a/6b: name the all-staff columns. For an HR admin every column is
      // editable (edits are attributed to the employee); for everyone else the
      // teammate columns are read-only.
      (gridCtx
        ? ui.controller.canEditAll
          ? " As HR you can edit any teammate's column — the hours still count as "
            + "that employee's; a ✎ marks a column you entered."
          : " Your teammates' columns are shown read-only — you edit only your own."
        : "");
  } else if (ui && ui.controller.canEditAll) {
    // G3 (2026-10-16): an admin-only overseer edits the all-staff grid but logs
    // no hours of their own.
    subtitle.textContent =
      "Oversight view — you can edit any teammate's hours (they still count as "
      + "that employee's; a ✎ marks a column you entered). You have no hours to log.";
  } else if (snapshot.snapshot_at) {
    subtitle.textContent =
      "A draft of what was worked on, week by week — to jog memory before " +
      `logging hours. Snapshot ${
        isoToShort(snapshot.snapshot_at)} ${snapshot.snapshot_at.slice(0, 4)}.`;
  } else {
    subtitle.textContent = "Nothing compiled yet — the next weekly run will populate this.";
  }
  main.appendChild(subtitle);

  const hasWeeks = snapshot.weeks.some((w) => w.items.length > 0);
  if (!hasWeeks && snapshot.no_activity.length === 0) {
    const empty = document.createElement("p");
    empty.className = "hours-log-empty";
    empty.textContent = "No activity recorded in the recent window.";
    main.appendChild(empty);
    root.appendChild(main);
    return;
  }

  // (c) Month ranges are the distinct billing months present in the snapshot
  // (Thursday rule), newest first — so the picker offers every available month,
  // not just current/last, and matches the agent's prebaked variants.
  const months = snapshotMonths(snapshot);

  // --- Filter bar (right-justified in the header). Two orthogonal controls:
  // the VIEW toggle (Internal vs client Billing) + the DATE RANGE. Both are
  // shared (viewRef/scopeRef) so the Generate button honors them. Billing is
  // inherently monthly, so its Date Range offers only the month options.
  const monthOpts = (): [WeekScope, string][] =>
    months.map((m) => [`month:${m}` as WeekScope, monthLabel(m)]);
  const rangeOpts = (): [WeekScope, string][] =>
    viewRef.value === "billing" || groupedRef.value
      ? monthOpts()
      : [["recent", "Most recent week"], ["all", "Last 4 weeks"], ...monthOpts()];

  const label = document.createElement("label");
  label.className = "hours-log-filter";
  const labelText = document.createElement("span");
  labelText.textContent = "Date Range";
  const select = document.createElement("select");
  label.appendChild(labelText);
  label.appendChild(select);

  const viewToggle = document.createElement("div");
  viewToggle.className = "hours-log-viewtoggle";
  viewToggle.setAttribute("role", "group");
  viewToggle.setAttribute("aria-label", "View");
  const viewBtns: { v: HoursView; el: HTMLButtonElement }[] = [];
  for (const [v, text] of [["internal", "Internal"], ["billing", "Billing"]] as
    [HoursView, string][]) {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "hours-log-viewbtn";
    b.textContent = text;
    b.addEventListener("click", () => {
      if (viewRef.value === v) return;
      viewRef.value = v;
      // Billing is monthly: coerce a rolling range to the most recent month.
      if (v === "billing" && scopeMonthKey(scopeRef.value) === null && months[0]) {
        scopeRef.value = `month:${months[0]}`;
      }
      syncControls();
      repaint();
      renderFreeze();  // HL-6d: the freeze control depends on the month scope
    });
    viewBtns.push({ v, el: b });
    viewToggle.appendChild(b);
  }

  // G2: the ungrouped/grouped export toggle (orthogonal to Internal/Billing).
  // Shown only when the project configures a rollup axis. Grouped is month-scoped
  // (the grouped-by-entity export exists per billing month), so toggling Grouped
  // coerces a rolling range to the most recent month, mirroring Billing.
  const groupBtns: { g: boolean; el: HTMLButtonElement }[] = [];
  let groupToggle: HTMLDivElement | null = null;
  if (snapshot.grouped_available) {
    groupToggle = document.createElement("div");
    groupToggle.className = "hours-log-viewtoggle";
    groupToggle.setAttribute("role", "group");
    groupToggle.setAttribute("aria-label", "Grouping");
    for (const [g, text] of [[false, "Ungrouped"], [true, "Grouped"]] as
      [boolean, string][]) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "hours-log-viewbtn";
      b.textContent = text;
      b.addEventListener("click", () => {
        if (groupedRef.value === g) return;
        groupedRef.value = g;
        if (g && scopeMonthKey(scopeRef.value) === null && months[0]) {
          scopeRef.value = `month:${months[0]}`;
        }
        syncControls();
        repaint();
        renderFreeze();
      });
      groupBtns.push({ g, el: b });
      groupToggle.appendChild(b);
    }
  }

  const syncControls = (): void => {
    for (const { v, el } of viewBtns) {
      const active = viewRef.value === v;
      el.classList.toggle("is-active", active);
      el.setAttribute("aria-pressed", String(active));
    }
    for (const { g, el } of groupBtns) {
      const active = groupedRef.value === g;
      el.classList.toggle("is-active", active);
      el.setAttribute("aria-pressed", String(active));
    }
    select.innerHTML = "";
    for (const [value, text] of rangeOpts()) {
      const o = document.createElement("option");
      o.value = value;
      o.textContent = text;
      if (value === scopeRef.value) o.selected = true;
      select.appendChild(o);
    }
  };
  syncControls();

  header.appendChild(viewToggle);
  if (groupToggle) header.appendChild(groupToggle);
  header.appendChild(label);

  // HL-6d — the period sign-off control. HR-only, shown when a specific billing
  // month is selected: freeze it (sign off, so the invoice can't drift) or, if
  // already frozen, unfreeze to edit. `renderFreeze` (defined after `repaint`)
  // repaints this + the cells; it is re-run on every scope/view change.
  const freezeWrap = document.createElement("div");
  freezeWrap.className = "hours-log-freeze";
  header.appendChild(freezeWrap);

  // --- §A — activity breakout + §B — no-activity. Both re-render together on a
  // filter change so §B's per-row week dropdown always offers the weeks in view.
  const sectionA = document.createElement("section");
  sectionA.className = "hours-log-section hours-log-activity";
  main.appendChild(sectionA);
  const sectionB = snapshot.no_activity.length > 0
    ? (() => {
        const s = document.createElement("section");
        s.className = "hours-log-section hours-log-no-activity";
        main.appendChild(s);
        return s;
      })()
    : null;

  const filteredWeeks = (): HoursLogWeek[] => {
    if (scopeRef.value === "recent") return snapshot.weeks.slice(0, 1);
    if (scopeRef.value === "all") return snapshot.weeks.slice(0, 4);
    // A month range scopes to that billing month (Thursday rule).
    const m = scopeMonthKey(scopeRef.value);
    if (m) return snapshot.weeks.filter((w) => billingMonth(w.week_end) === m);
    return snapshot.weeks;
  };

  // The Grouped read-out's hours source for the weeks in view: PER WEEK, gather
  // that week's grid rows (self + teammates, live edits preferred over the persisted
  // payload) paired with that week's Items (for that week's association tags). An
  // Item row (base ∈ that week's Items) routes per entity — so an Item tagged in a
  // later run than the week it was logged in attributes the SAME way the billing
  // workbook does. A NON-Item row (custom "Also worked", or an orphan key for an
  // Item not tracked that week) is `grid.additional` in the workbook → it bills
  // under Unallocated, so it is surfaced here as an Unallocated "Also worked" line
  // (summed by label) — WITHOUT it the on-screen Σ would silently under-report the
  // billed total. null when the viewer has no entry data (off-roster read-only).
  const buildGroupedData = (
    wks: HoursLogWeek[],
  ): { hours: GroupedHours; alsoWorked: Map<string, { label: string; hours: number }[]> } | null => {
    if (!ui && !gridCtx) return null;
    const known = new Set((snapshot.associations ?? []).map((a) => a.slug));
    // Non-Item "Also worked" rows (custom add-a-rows, orphan item-keys) all bill to
    // Unallocated — the per-association tagging of ad-hoc rows was deferred.
    const alsoBySection = new Map<string, Map<string, number>>();
    const addAlso = (section: string, label: string, h: number): void => {
      const m = alsoBySection.get(section) ?? new Map<string, number>();
      m.set(label, (m.get(label) ?? 0) + h);
      alsoBySection.set(section, m);
    };
    const perWeek = wks.map((wk) => {
      const wslugs = new Set(wk.items.map((it) => it.slug));
      const rows: { row_key: string; hours: number }[] = [];
      const push = (rs: HoursEntryRowInput[] | undefined): void => {
        for (const r of rs ?? []) {
          const h = r.hours ?? 0;
          if (h <= 0) continue;
          const base = r.row_key.startsWith("item:")
            ? r.row_key.slice(5).split(":")[0] : null;
          if (base && wslugs.has(base)) {
            rows.push({ row_key: r.row_key, hours: h });
          } else {
            const label = (r.label || "").trim() || r.row_key;
            addAlso(GROUPED_UNALLOCATED, label, h);
          }
        }
      };
      if (gridCtx) {
        for (const m of gridCtx.members) {
          const mdl = ui
            ? m.is_self
              ? ui.controller.week(wk.week_end)
              : ui.controller.peekMemberModel(m.id, wk.week_end)
            : undefined;
          push(mdl ? mdl.serializeRows()
            : m.weeks.find((w) => w.week_end === wk.week_end)?.rows);
        }
      } else if (ui) {
        push(ui.controller.week(wk.week_end)?.serializeRows());
      }
      return { items: wk.items, rows };
    });
    const alsoWorked = new Map<string, { label: string; hours: number }[]>();
    for (const [section, m] of alsoBySection) {
      alsoWorked.set(section, [...m.entries()]
        .map(([label, hours]) => ({ label, hours: Math.round(hours * 4) / 4 })));
    }
    return { hours: routeGroupedHours(perWeek, known), alsoWorked };
  };

  const repaint = (): void => {
    const weeks = filteredWeeks();
    const billing = viewRef.value === "billing";
    // G2 (Phase A): the on-screen grouped read-out — §A re-organizes by
    // association (read-only) instead of by week. Gated on the project
    // configuring a rollup axis (grouped_available).
    const grouped = Boolean(groupedRef.value && snapshot.grouped_available);
    // Both sections re-render, so every refresher is stale — reset before the
    // fresh cards/rows re-register (avoids closures pointing at detached DOM).
    if (ui) ui.refreshers = [];
    // HL-6d: re-read the frozen set from the controller each repaint so a
    // freeze/unfreeze (which mutates controller.lockedMonths) re-renders cells.
    if (ui) ui.frozenMonths = new Set(Object.keys(ui.controller.lockedMonths));

    // §B placement (operator 2026-07-23): a logged no-activity item surfaces
    // under every week it has hours in (multi-week), while ALSO staying in the
    // bottom §B list so it can be added to another week.
    const placedByWeek = ui
      ? ui.controller.quietItemsByWeek(
          snapshot.weeks.map((w) => w.week_end), snapshot.no_activity,
        )
      : new Map<string, HoursLogNoActivityItem[]>();

    sectionA.innerHTML = "";
    // The entity order + name map for the Grouped editable grid (section order, name
    // lookup, split-row narratives, tag options). CONFIGURED associations ONLY — the
    // bill (buildGroupedData / the workbook) routes a live-but-unconfigured tag to
    // Unallocated, so the grid must not render a section for it, offer it as a tag
    // target, or treat its split as "known" (else the editor disagrees with the bill).
    const nameOf = new Map(
      (snapshot.associations ?? []).map((a) => [a.slug, a.name] as const),
    );
    const prettySlugG = (slug: string): string =>
      slug.replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    const order: string[] = (snapshot.associations ?? []).map((a) => a.slug);
    // The association context — carried on the all-staff grid (gridCtx) so the
    // EDITABLE Grouped grid can lay rows out per entity (split keys) + offer the
    // inline Tag-to-association affordance on Unallocated rows. Null when there is
    // no all-staff grid (the read-out path doesn't need it).
    const groupedCtx: GroupedGridCtx | null = gridCtx
      ? {
          order,
          nameFor: (slug) => nameOf.get(slug) ?? prettySlugG(slug),
          entityLabel: snapshot.entity_label || "Association",
          runId: snapshot.run_id ?? null,
          applyTag: (slug, chosen, aiSlugs) => {
            for (const w of snapshot.weeks) {
              for (const it of w.items) {
                if (it.slug === slug) applyOptimisticTag(it, chosen, aiSlugs);
              }
            }
          },
        }
      : null;

    if (grouped && gridCtx && !billing) {
      // INTERNAL + Grouped (all-staff editor): the EDITABLE by-association ENTRY grid
      // — per-entity split-key rows + Unallocated + the inline Tag. Entry here writes
      // the association's split key, so a person's hours land in the matching workbook
      // column. This is the ONE place hours are entered. One grid per week in view.
      if (weeks.length === 0) {
        const none = document.createElement("p");
        none.className = "hours-log-week-empty";
        none.textContent = "No tracked activity in this period.";
        sectionA.appendChild(none);
      } else {
        for (const week of weeks) {
          sectionA.appendChild(renderWeek(
            week, ui, placedByWeek.get(week.week_end) ?? [], false, gridCtx,
            groupedCtx, true, false,
          ));
        }
      }
    } else if (grouped) {
      // BILLING + Grouped (the read-only by-association BILLING view, all-staff summed),
      // OR a read-only off-roster viewer / single logger: the by-association READ-OUT.
      // Hours when there is entry data (routed per entity exactly like the workbook,
      // 0-hour dropped — the billing rule — incl. the "Also worked" lines), else
      // narratives only. The Grouped BILLING .xlsx exports the same model.
      renderGroupedActivity(
        sectionA, weeks, snapshot, buildGroupedData(weeks),
      );
    } else if (weeks.length === 0) {
      const none = document.createElement("p");
      none.className = "hours-log-week-empty";
      none.textContent = "No tracked activity in this period.";
      sectionA.appendChild(none);
    } else {
      // Ungrouped (flat by-item). On an ASSOCIATION-axis project the all-staff grid is a
      // READ-ONLY rollup — each Item's total (base + Σ per-entity splits), summed from the
      // Grouped entry grid (entry is in Grouped). A single logger (no all-staff grid) keeps
      // the editable card path. Billing is the read-only client bill. BUT a project with NO
      // rollup axis (`grouped_available` false) has NO Grouped toggle, so the flat grid stays
      // its EDITABLE entry surface — else its roster would have nowhere to log hours.
      const flatReadOnly = Boolean(gridCtx && !billing && snapshot.grouped_available);
      for (const week of weeks) {
        sectionA.appendChild(
          renderWeek(
            week, ui, placedByWeek.get(week.week_end) ?? [], billing, gridCtx,
            null, false, flatReadOnly,
          ),
        );
      }
    }
    if (sectionB) {
      sectionB.innerHTML = "";
      // §B always lists ALL no-activity items (operator 2026-07-23): a logged
      // item stays here so it can be added to another week too. Hidden on the
      // billing preview — a no-activity (no-hours) item isn't billable — and on
      // the grouped read-out (which is scoped to the association axis, not the
      // open-but-quiet memory-jog).
      if (!billing && !grouped) {
        renderNoActivityInto(sectionB, snapshot.no_activity, ui, weeks);
      }
    }
    if (ui) ui.refreshAll();
  };
  if (ui) ui.repaint = repaint;

  // HL-6d — (re)render the HR freeze/unfreeze control for the selected month.
  const renderFreeze = (): void => {
    freezeWrap.innerHTML = "";
    const m = ui && ui.controller.canEditAll ? scopeMonthKey(scopeRef.value) : null;
    if (!ui || !m) return;
    const lock = ui.controller.lockedMonths[m];
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "hours-log-freezebtn";
    btn.textContent = lock
      ? `🔓 Unfreeze ${monthName(m)}`
      : `🔒 Freeze ${monthName(m)} (sign off)`;
    if (lock) {
      const note = document.createElement("span");
      note.className = "hours-log-freeze-note";
      note.textContent = lock.locked_by_name
        ? `Billed — locked by ${lock.locked_by_name}`
        : "Billed — locked";
      freezeWrap.appendChild(note);
    }
    btn.addEventListener("click", async () => {
      btn.disabled = true;
      try {
        const res = await freezeFn(m, !lock);
        ui.controller.setLockedMonths(res.locked_months);
      } catch {
        btn.disabled = false;
        return;
      }
      renderFreeze();
      repaint();  // re-renders cells against the new frozen set
    });
    freezeWrap.appendChild(btn);
  };

  select.addEventListener("change", () => {
    scopeRef.value = select.value as WeekScope;
    repaint();
    renderFreeze();
  });
  repaint();
  renderFreeze();

  root.appendChild(main);
}

// Route a raw hours-field value into the model with the blank-vs-0 distinction
// in ONE place: a blank / non-numeric / negative field CLEARS the row
// (unentered); any other parseable value, INCLUDING 0, sets it (an explicit 0 =
// "addressed, no time" — a real, persisted value). Every editable cell uses this.
export function commitHoursInput(
  model: WeekEntryModel, rowKey: string, label: string, raw: string,
): void {
  const s = raw.trim();
  const n = parseFloat(s);
  if (s === "" || !isFinite(n) || n < 0) model.clearHours(rowKey);
  else model.setHours(rowKey, label, normalizeHours(s));
}

// Display value for a cell: the explicit hours ("0" for a present-0), or blank
// for an absent (unentered) row.
function cellDisplayValue(model: WeekEntryModel, rowKey: string): string {
  return model.hasEntry(rowKey) ? String(model.getHours(rowKey)) : "";
}

// One right-aligned quarter-hour input bound to a WeekEntryModel row. Blank =
// unentered (no row); an explicit "0" = "addressed, no time" (a real, persisted
// value). `inputmode=decimal` gives a numeric phone keypad. Commits on input; a
// global refresh follows. `label` may be a thunk so an editable custom row's
// rename travels with the entry. `onEmptyBlur` fires when the field blurs EMPTY
// (unentered) — a placed §B row uses it to return to the no-activity list once
// its hours are cleared (an explicit 0 keeps it, so it does NOT fire on 0).
// HL-6c: a lock chip on a cell HR has locked. As a `<span>` (🔒) it's a passive
// indicator with the "entered by X" tooltip; given `unlock` (HR only) it's a 🔓
// button that returns the cell to the employee.
function lockIndicator(editorName: string | null, unlock?: () => void): HTMLElement {
  const who = editorName ? `Entered by ${editorName}` : "Entered by HR";
  if (unlock) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "hours-log-lock hours-log-lock-btn";
    btn.textContent = "🔓";
    btn.title = `${who} · locked — click to unlock`;
    btn.setAttribute("aria-label", "Unlock this cell");
    btn.addEventListener("click", unlock);
    return btn;
  }
  const el = document.createElement("span");
  el.className = "hours-log-lock";
  el.textContent = "🔒";
  el.title = `${who} · locked`;
  return el;
}

// HL-6d: the hover tooltip on a cell whose billing month is frozen (period
// sign-off). No visual icon — a frozen period disables EVERY cell, so a per-cell
// lock icon is redundant noise (operator 2026-09-26); the dashed disabled box +
// the header "Billed — locked by X" note carry the state. The tooltip gives
// context (esp. in the rolling view, where the header note isn't shown).
function frozenCellTitle(ui: EntryUI, weekEnd: string): string {
  const by = ui.controller.lockedMonths[billingMonth(weekEnd)]?.locked_by_name;
  return by ? `Billed — period locked by ${by}` : "Billed — period locked";
}

function makeHoursCell(
  model: WeekEntryModel, rowKey: string, label: string | (() => string), ui: EntryUI,
  // `ariaLabel` overrides the screen-reader text only — the STORED label (persisted
  // as the billing fallback name) stays `label`. So a Grouped row can read "Pool —
  // Master" to a screen reader while billing orphans fall back to the plain name.
  opts: { onEmptyBlur?: () => void; hrEditable?: boolean; ariaLabel?: string } = {},
): HTMLElement {
  const labelOf = (): string => (typeof label === "function" ? label() : label);
  const locked = model.isLocked(rowKey);
  const input = document.createElement("input");
  input.className = "hours-log-cell";
  input.type = "text";
  input.inputMode = "decimal";
  input.setAttribute("aria-label", `Hours for ${opts.ariaLabel ?? labelOf()}`);
  input.value = cellDisplayValue(model, rowKey);
  // HL-6d: a frozen billing month (period sign-off) is READ-ONLY for EVERYONE —
  // owner AND HR (D1: a true sign-off; HR unfreezes the month to edit). This
  // takes precedence over the per-cell lock/HR-editable paths below. The Worker
  // PUT enforces it regardless of the disabled input (KILL). Misc freezes too
  // (makeMiscCell) — a frozen period freezes everything shown. NO per-cell icon:
  // the whole period is frozen, so a lock on every cell is just noise (operator
  // 2026-09-26); the disabled/dashed box + the header "Billed — locked by X" note
  // carry it, with a hover tooltip for context in the rolling view.
  if (ui.frozenMonths.has(billingMonth(model.weekEnd))) {
    input.disabled = true;
    input.classList.add("hours-log-cell-locked", "hours-log-cell-frozen");
    input.title = frozenCellTitle(ui, model.weekEnd);
    return input;
  }
  // HL-6c: a locked cell is READ-ONLY to the owner (disabled + a 🔒 chip); an HR
  // viewer keeps editing and gets a 🔓 unlock control. The Worker enforces this
  // regardless of the input's disabled state (KILL — never trust the client).
  if (locked && !opts.hrEditable) {
    input.disabled = true;
    input.classList.add("hours-log-cell-locked");
    const wrap = document.createElement("span");
    wrap.className = "hours-log-cell-wrap";
    wrap.appendChild(lockIndicator(model.lockEditor(rowKey)));
    wrap.appendChild(input);
    return wrap;
  }
  input.addEventListener("input", () => {
    commitHoursInput(model, rowKey, labelOf(), input.value);
    ui.refreshAll();
  });
  // Normalize the display to the grid on blur (e.g. "1.3" → "1.25"). An explicit
  // "0" stays "0"; a blanked field stays blank and fires onEmptyBlur (a placed
  // §B row uses this to drop from the week — distinct from an addressed-0).
  input.addEventListener("blur", () => {
    input.value = cellDisplayValue(model, rowKey);
    if (!model.hasEntry(rowKey) && opts.onEmptyBlur) opts.onEmptyBlur();
  });
  if (!opts.hrEditable) return input;  // owner, unlocked → plain editable input
  // HR: an always-editable input + a REACTIVE lock chip, so the 🔓 tracks the
  // live lock state — an edit auto-locks it (chip appears), the unlock click
  // clears it — with no reload or repaint (a plain refresh repaints the chip).
  const wrap = document.createElement("span");
  wrap.className = "hours-log-cell-wrap";
  const chipHost = document.createElement("span");
  chipHost.className = "hours-log-lock-host";
  const paintChip = (): void => {
    chipHost.textContent = "";
    if (model.isLocked(rowKey)) {
      chipHost.appendChild(
        lockIndicator(model.lockEditor(rowKey), () => {
          model.unlockRow(rowKey);
          ui.refreshAll();
        }),
      );
    }
  };
  paintChip();
  ui.refreshers.push(paintChip);
  wrap.appendChild(chipHost);
  wrap.appendChild(input);
  return wrap;
}

// A quarter-hour hours cell paired with a bucket setter (used by misc rows).
// HL-6c: honors the per-bucket lock like makeHoursCell.
function makeMiscCell(
  model: WeekEntryModel, bucket: keyof MiscBuckets, ui: EntryUI,
  opts: { hrEditable?: boolean } = {},
): HTMLElement {
  const locked = model.isMiscLocked(bucket);
  const input = document.createElement("input");
  input.className = "hours-log-cell";
  input.type = "text";
  input.inputMode = "decimal";
  input.setAttribute("aria-label", MISC_LABELS[bucket]);
  const cur = model.misc[bucket];
  input.value = cur > 0 ? String(cur) : "";
  // HL-6d: a frozen month freezes Non-Project time too (read-only for everyone) —
  // a signed-off period has no editable cells. Precedes the per-bucket lock. No
  // per-cell icon (see makeHoursCell). The Worker rejects a misc change on a
  // frozen month regardless (KILL).
  if (ui.frozenMonths.has(billingMonth(model.weekEnd))) {
    input.disabled = true;
    input.classList.add("hours-log-cell-locked", "hours-log-cell-frozen");
    input.title = frozenCellTitle(ui, model.weekEnd);
    return input;
  }
  if (locked && !opts.hrEditable) {
    input.disabled = true;
    input.classList.add("hours-log-cell-locked");
    const wrap = document.createElement("span");
    wrap.className = "hours-log-cell-wrap";
    wrap.appendChild(lockIndicator(model.miscEditor(bucket)));
    wrap.appendChild(input);
    return wrap;
  }
  input.addEventListener("input", () => {
    model.setMisc(bucket, normalizeHours(input.value));
    ui.refreshAll();
  });
  input.addEventListener("blur", () => {
    input.value = model.misc[bucket] > 0 ? String(model.misc[bucket]) : "";
  });
  if (!opts.hrEditable) return input;  // owner, unlocked → plain editable input
  // HR: reactive lock chip (mirrors makeHoursCell).
  const wrap = document.createElement("span");
  wrap.className = "hours-log-cell-wrap";
  const chipHost = document.createElement("span");
  chipHost.className = "hours-log-lock-host";
  const paintChip = (): void => {
    chipHost.textContent = "";
    if (model.isMiscLocked(bucket)) {
      chipHost.appendChild(
        lockIndicator(model.miscEditor(bucket), () => {
          model.unlockMiscBucket(bucket);
          ui.refreshAll();
        }),
      );
    }
  };
  paintChip();
  ui.refreshers.push(paintChip);
  wrap.appendChild(chipHost);
  wrap.appendChild(input);
  return wrap;
}

// §7 "Also worked this week" section (operator 2026-07-23 #2): a labelled,
// category-styled block after the briefing items, separated by a thin line
// (like the non-project footer). Holds the placed "open, but no recent" items
// logged against this week (#4) + the add-a-row for work the briefing missed.
// At category indent (not nested under the last item). Custom labels editable.
function renderProjectExtras(
  weekEnd: string, ui: EntryUI, placedQuiet: HoursLogNoActivityItem[] = [],
  billing = false,
): HTMLElement | null {
  const model = ui.controller.week(weekEnd);
  if (!model) return null;
  // Billing preview: no add-a-row (it's a review, not entry) and skip the whole
  // block when there's nothing to bill under "Also worked" (no empty header).
  if (billing && placedQuiet.length === 0 && model.customRows().length === 0) {
    return null;
  }
  const wrap = document.createElement("div");
  wrap.className = "hours-log-project-extras";

  const head = document.createElement("div");
  head.className = "hours-log-alsoworked-head";
  head.textContent = ALSO_WORKED_LABEL;
  wrap.appendChild(head);

  // Placed §B items — rendered like a project row, tagged so it's clear they
  // came from the open-items list. CLEARING the cell (blank) drops it from this
  // week (it stays in the §B list below for logging against another week); an
  // explicit 0 keeps it here as "addressed, no time."
  for (const item of placedQuiet) {
    const row = document.createElement("div");
    row.className = "hours-log-entry-row hours-log-placed-row";
    const lbl = document.createElement("span");
    lbl.className = "hours-log-entry-label";
    lbl.textContent = item.name;
    const tag = document.createElement("span");
    tag.className = "hours-log-placed-tag";
    tag.textContent = "open item";
    lbl.appendChild(tag);
    row.appendChild(lbl);
    row.appendChild(makeHoursCell(model, `item:${item.slug}`, item.name, ui, {
      onEmptyBlur: () => ui.repaint(),  // cleared → dropped from this week
    }));
    wrap.appendChild(row);
  }

  const customHost = document.createElement("div");
  customHost.className = "hours-log-custom-rows";
  // Re-render already-committed custom rows (survive a filter repaint / reload).
  for (const { rowKey, label } of model.customRows()) {
    customHost.appendChild(renderCustomRow(model, rowKey, label, ui));
  }
  wrap.appendChild(customHost);

  if (billing) return wrap;  // review view: no add-a-row entry chrome
  // HL-6d: no add-a-row on a frozen (billed) month — you can't add new billable
  // work to a signed-off period (the placed/committed rows above are already
  // frozen read-only via makeHoursCell; the Worker 403s an add regardless).
  if (ui.frozenMonths.has(billingMonth(weekEnd))) return wrap;

  const addRow = document.createElement("div");
  addRow.className = "hours-log-entry-row hours-log-add-row";
  const addLabel = document.createElement("input");
  addLabel.className = "hours-log-add-label";
  addLabel.type = "text";
  addLabel.placeholder = "Worked on something not listed? Add a project row";
  addLabel.setAttribute("aria-label", "New project row description");
  const addHours = document.createElement("input");
  addHours.className = "hours-log-cell";
  addHours.type = "text";
  addHours.inputMode = "decimal";
  addHours.setAttribute("aria-label", "New project row hours");
  const commitAdd = (): void => {
    const label = addLabel.value.trim();
    const raw = addHours.value.trim();
    const n = parseFloat(raw);
    // A new custom line needs a label + a real hours value; an explicit 0
    // ("addressed, no time") is allowed, a blank / garbage / negative field is
    // not (you don't add an unentered line).
    if (!label || raw === "" || !isFinite(n) || n < 0) return;
    const key = model.addCustomRow(label, normalizeHours(raw));
    customHost.appendChild(renderCustomRow(model, key, label, ui));
    addLabel.value = "";
    addHours.value = "";
    ui.refreshAll();
  };
  addHours.addEventListener("change", commitAdd);
  addRow.appendChild(addLabel);
  addRow.appendChild(addHours);
  wrap.appendChild(addRow);
  return wrap;
}

// The Save + live-total bar for a week. Shared by the card footer AND the
// all-staff grid so both save through the same path. Registers a refresher that
// keeps the total + dirty state current on any edit anywhere in the week.
function buildSaveBar(
  weekEnd: string, ui: EntryUI, model: WeekEntryModel,
): HTMLElement {
  const bar = document.createElement("div");
  bar.className = "hours-log-save-bar";
  const total = document.createElement("span");
  total.className = "hours-log-week-total";
  const dirtyDot = document.createElement("span");
  dirtyDot.className = "hours-log-dirty-dot";
  dirtyDot.hidden = true;
  const saveBtn = document.createElement("button");
  saveBtn.type = "button";
  saveBtn.className = "hours-log-save-btn";
  saveBtn.textContent = "Save week";
  saveBtn.title =
    "Save this week's hours to the server. Entries aren't saved automatically — " +
    "each week saves on its own.";
  const err = document.createElement("span");
  err.className = "hours-log-save-error";
  err.hidden = true;
  saveBtn.addEventListener("click", async () => {
    saveBtn.disabled = true;
    saveBtn.textContent = "Saving…";
    err.hidden = true;
    try {
      await ui.controller.save(weekEnd);
      // On success the model is clean → refreshAll owns the disabled state; the
      // HL-6c lock chips are reactive (refreshers), so they reflect any applied
      // unlocks without a full repaint.
      saveBtn.textContent = "Saved";
      ui.refreshAll();
    } catch (e) {
      err.hidden = false;
      err.textContent = e instanceof Error ? e.message : String(e);
      saveBtn.textContent = "Save week";
      saveBtn.disabled = false;  // re-enable only on failure, so a retry is possible
    }
  });
  bar.appendChild(total);
  bar.appendChild(dirtyDot);
  bar.appendChild(saveBtn);
  bar.appendChild(err);
  ui.refreshers.push(() => {
    const t = model.total();
    total.textContent = `Your total: ${t} h`;
    // HL-6b: dirty tracks the whole week — the self column OR any teammate column
    // an HR viewer edited — so one "Save week" flushes everything HR touched.
    const dirty = ui.controller.weekDirty(weekEnd);
    dirtyDot.hidden = !dirty;
    saveBtn.disabled = !dirty;
    if (dirty && saveBtn.textContent === "Saved") saveBtn.textContent = "Save week";
  });
  return bar;
}

// The per-week entry footer: NON-PROJECT (misc) buckets + live total + Save.
// (operator #1) the "Non-project time" head sits at the phase/category indent,
// not indented under the item list. Registers a refresher so any edit in the
// week keeps the total + Save state current.
function renderEntryFooter(weekEnd: string, ui: EntryUI): HTMLElement {
  const model = ui.controller.week(weekEnd);
  const footer = document.createElement("div");
  footer.className = "hours-log-entry-footer";
  if (!model) return footer;

  const miscHead = document.createElement("div");
  miscHead.className = "hours-log-misc-head";
  miscHead.textContent = "Non-project time";
  footer.appendChild(miscHead);
  const miscRows = document.createElement("div");
  miscRows.className = "hours-log-misc-rows";
  for (const bucket of MISC_BUCKETS) {
    const row = document.createElement("div");
    row.className = "hours-log-entry-row";
    const lbl = document.createElement("span");
    lbl.className = "hours-log-entry-label";
    lbl.textContent = MISC_LABELS[bucket];
    row.appendChild(lbl);
    row.appendChild(makeMiscCell(model, bucket, ui));
    miscRows.appendChild(row);
  }
  footer.appendChild(miscRows);
  footer.appendChild(buildSaveBar(weekEnd, ui, model));
  return footer;
}

// A small "×" button to remove a self-added row. The caller wires the action
// (clear the row from the model + repaint); Save then persists the removal.
function makeRowDeleteButton(onClick: () => void): HTMLButtonElement {
  const del = document.createElement("button");
  del.type = "button";
  del.className = "hours-log-row-delete";
  del.textContent = "×";
  del.title = "Remove this row";
  del.setAttribute("aria-label", "Remove this row");
  del.addEventListener("click", onClick);
  return del;
}

// A committed custom (add-a-row) entry — editable label (operator #3: click to
// rename) + hours cell + a delete (×) to remove the row.
function renderCustomRow(
  model: WeekEntryModel, rowKey: string, label: string, ui: EntryUI,
): HTMLElement {
  const row = document.createElement("div");
  row.className = "hours-log-entry-row hours-log-custom-row";
  const lbl = document.createElement("input");
  lbl.className = "hours-log-custom-label";
  lbl.type = "text";
  lbl.value = label;
  lbl.setAttribute("aria-label", "Row description");
  lbl.addEventListener("input", () => {
    // Keep the model label + the hours cell's aria in sync as it's renamed.
    model.setLabel(rowKey, lbl.value.trim() || label);
    ui.refreshAll();
  });
  row.appendChild(lbl);
  row.appendChild(makeHoursCell(model, rowKey, () => lbl.value.trim() || label, ui));
  row.appendChild(makeRowDeleteButton(() => {
    model.clearHours(rowKey);
    ui.repaint();
  }));
  return row;
}

// #89 — phase band labels/order (mirrors the briefing's #37 phase tier).
const PHASE_LABELS: Record<string, string> = {
  pre_development: "Pre-Development",
  pre_construction: "Pre-Construction",
  construction: "Construction",
  general: "General",
};
const PHASE_ORDER = ["pre_development", "pre_construction", "construction", "general"];
function phaseSortKey(p: string | null | undefined): number {
  const i = PHASE_ORDER.indexOf(p ?? "");
  return i < 0 ? PHASE_ORDER.length : i;
}

// Generic Phase → Category → Item grouping, shared by §A (activity) and §B
// (no-activity). Phase renders as a quiet eyebrow above the Category header
// (Category is the visual anchor — client 2026-07-01 (d)). Phase bands activate
// only when >=1 item carries a non-null, non-'general' phase.
interface Grouped { phase?: string | null; thread_name: string | null }
function appendPhaseCategoryGroups<T extends Grouped>(
  container: HTMLElement, items: T[], renderItem: (it: T) => HTMLElement,
): void {
  const renderCats = (group: T[]): HTMLElement => {
    const wrap = document.createElement("div");
    wrap.className = "hours-log-cats";
    const byCat = new Map<string, T[]>();
    for (const it of group) {
      const cat = it.thread_name ?? "Uncategorized";
      const arr = byCat.get(cat) ?? [];
      arr.push(it);
      byCat.set(cat, arr);
    }
    for (const [cat, catItems] of byCat) {
      const head = document.createElement("div");
      head.className = "hours-log-cat";
      head.textContent = cat;
      wrap.appendChild(head);
      const list = document.createElement("div");
      list.className = "hours-log-items";
      for (const it of catItems) list.appendChild(renderItem(it));
      wrap.appendChild(list);
    }
    return wrap;
  };

  const showPhases = items.some((it) => it.phase && it.phase !== "general");
  if (!showPhases) {
    container.appendChild(renderCats(items));
    return;
  }
  const byPhase = new Map<string | null | undefined, T[]>();
  for (const it of items) {
    const arr = byPhase.get(it.phase) ?? [];
    arr.push(it);
    byPhase.set(it.phase, arr);
  }
  const phases = [...byPhase.keys()].sort((a, b) => phaseSortKey(a) - phaseSortKey(b));
  for (const phase of phases) {
    const band = document.createElement("div");
    band.className = "hours-log-phase";
    band.textContent = PHASE_LABELS[phase ?? ""] ?? "General";
    container.appendChild(band);
    container.appendChild(renderCats(byPhase.get(phase) as T[]));
  }
}

// G2 (Phase A) — the ON-SCREEN grouped read-out. When Grouped is on, §A
// re-organizes BY ASSOCIATION (billing entity) instead of by week: a read-only
// mirror of the grouped export — Entity → Phase → Category → Item. A
// multi-association Item appears under EACH of its entities (flagged: the
// per-(Item × entity) hours split is Phase-2 input capture, not derived here).
// Untagged Items fall under "Unallocated". Entry stays in the flat (Ungrouped)
// view; this readout renders read-only cards (no hours cells).
export const GROUPED_UNALLOCATED = "__unallocated__";

export interface GroupedEntity {
  key: string;               // association slug, or GROUPED_UNALLOCATED
  name: string;              // display name (config name, or a prettified slug)
  unallocated: boolean;
  items: HoursLogItem[];     // a multi-association Item appears under several
}

// The pure bucketing — unit-tested (the render around it is a screenshot
// surface, per reference-spa-verification-surface). Entity order = the config
// order carried on the snapshot; any tag not in that list is appended
// first-seen. Items are deduped by slug across the given weeks (grouped is
// month-scoped, so an Item shows once — keep its most-recent instance, weeks
// newest-first). A multi-association Item is placed under EACH of its entities;
// an untagged Item under Unallocated. Only non-empty entities are returned, in
// order, with Unallocated last.
export function groupHoursByAssociation(
  weeks: HoursLogWeek[], snapshot: HoursLogSnapshot,
): GroupedEntity[] {
  const bySlug = new Map<string, HoursLogItem>();
  for (const w of weeks) {
    for (const it of w.items) if (!bySlug.has(it.slug)) bySlug.set(it.slug, it);
  }
  const items = [...bySlug.values()];

  // CONFIGURED associations ONLY — a live-but-unconfigured tag bills to Unallocated
  // (routeGroupedHours / _week_bucket_hours), so the read-out must not render a
  // section for it; it routes to Unallocated below, matching the bill.
  const order: string[] = [];
  const nameOf = new Map<string, string>();
  for (const a of snapshot.associations ?? []) {
    if (!nameOf.has(a.slug)) { order.push(a.slug); nameOf.set(a.slug, a.name); }
  }
  const known = new Set(order);
  // Degenerate: a rollup project with NO associations metadata — fall back to the
  // items' tags as sections (parity with routeGroupedHours' empty-known = no filter).
  if (known.size === 0) {
    const seen = new Set<string>();
    for (const it of items) {
      for (const slug of it.association_tags ?? []) {
        if (!seen.has(slug)) { seen.add(slug); order.push(slug); }
      }
    }
  }
  const isKnown = (t: string): boolean => known.size === 0 || known.has(t);
  const prettySlug = (slug: string): string =>
    slug.replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const nameFor = (slug: string): string => nameOf.get(slug) ?? prettySlug(slug);

  const buckets = new Map<string, HoursLogItem[]>();
  const push = (key: string, it: HoursLogItem): void => {
    const arr = buckets.get(key) ?? [];
    arr.push(it);
    buckets.set(key, arr);
  };
  for (const it of items) {
    const tags = it.association_tags ?? [];
    const cfg = tags.filter(isKnown);
    for (const slug of cfg) push(slug, it);
    // Action-level Unallocated: an Item's residual lands here ALONGSIDE its tagged
    // entities. Placed here unless it has EXACTLY ONE configured tag and no others —
    // a fully-untagged Item (0 tags), a multi Item (its untagged base routes here),
    // AND an Item carrying any UNCONFIGURED tag (that work bills to Unallocated).
    // Mirrors routeGroupedHours' base routing. (The render skips an Item placed here
    // whose residual narrative AND base hours are both empty.)
    if (cfg.length !== 1 || cfg.length !== tags.length) push(GROUPED_UNALLOCATED, it);
  }

  const out: GroupedEntity[] = [];
  for (const slug of order) {
    const its = buckets.get(slug);
    if (its && its.length > 0) {
      out.push({ key: slug, name: nameFor(slug), unallocated: false, items: its });
    }
  }
  const unalloc = buckets.get(GROUPED_UNALLOCATED);
  if (unalloc && unalloc.length > 0) {
    out.push({
      key: GROUPED_UNALLOCATED, name: "Unallocated", unallocated: true, items: unalloc,
    });
  }
  return out;
}


// --- G2 Phase B: the item-centric split-ENTRY decomposition (pure, unit-tested;
// the grid render around it is the screenshot surface). A MULTI-association Item
// (its Actions span ≥2 entities) expands into one editable split row per entity,
// plus an Unallocated row IFF some of its Actions carry no association. Each row's
// narrative is composed from the Item's Actions scoped to that entity.
export interface ItemSplitRow {
  assoc: string | null;   // association slug, or null for the untagged residual
  rowKey: string;         // item:<slug>:<assoc>  |  item:<slug>:__unallocated__
  name: string;           // entity display name, or "Unallocated"
  narrative: string;      // joined text of the Actions scoped to this entity
}

export function itemIsMultiAssociation(item: HoursLogItem): boolean {
  return (item.association_tags?.length ?? 0) >= 2;
}

export function splitItemByAssociation(
  item: HoursLogItem, order: string[], nameFor: (slug: string) => string,
): ItemSplitRow[] {
  const tags = item.association_tags ?? [];
  const rank = (slug: string): number => {
    const i = order.indexOf(slug);
    return i < 0 ? order.length : i;
  };
  const ordered = [...tags].sort((a, b) => rank(a) - rank(b));
  const compose = (pred: (a: HoursLogAction) => boolean): string =>
    (item.actions ?? []).filter(pred).map((a) => a.text).filter(Boolean).join("; ");
  const rows: ItemSplitRow[] = ordered.map((slug) => ({
    assoc: slug,
    rowKey: `item:${item.slug}:${slug}`,
    name: nameFor(slug),
    narrative: compose((a) => (a.association_tags ?? []).includes(slug)),
  }));
  // The Unallocated ("None") bucket IS the base key `item:<slug>` — hours not yet
  // assigned to an entity, INCLUDING any legacy (pre-split) hours logged before
  // splitting existed. So legacy hours are unallocated by construction (no
  // migration, no double-count). Always present on a multi-Item: a parking spot
  // + the inline-tag cue. Narrative = the untagged Actions' text (may be empty).
  rows.push({
    assoc: null,
    rowKey: `item:${item.slug}`,
    name: "None",
    narrative: compose((a) => (a.association_tags ?? []).length === 0),
  });
  return rows;
}

// G2 Phase B — inline tagging (pure, unit-tested). The UNTAGGED Actions (empty
// association_tags, with an ai_slug) are the work not yet assigned to an entity —
// the tag targets. The options are the project associations NOT already on the
// Item. Applying a tag moves ONLY those untagged Actions to the chosen entity and
// adds it to the Item's union (the already-tagged Actions are left intact, so the
// per-entity narratives are preserved).
export function inlineTagTargets(item: HoursLogItem): HoursLogAction[] {
  return (item.actions ?? []).filter(
    (a) => (a.association_tags ?? []).length === 0 && Boolean(a.ai_slug),
  );
}
// The tag picker offers EVERY configured association — an Item's untagged Actions
// may belong to any entity, INCLUDING one the Item already carries for its other
// Actions (e.g. a spanning Item with a Master action + an untagged action that is
// also Master). The `item` param is kept for the signature; no option is excluded.
export function inlineTagOptions(_item: HoursLogItem, order: string[]): string[] {
  return [...order];
}
// Replay a tag onto THIS copy of the Item by the SPECIFIC Action ai_slugs that
// were POSTed (not a per-week re-derivation) — so a week that does not carry one
// of those Actions is left untouched, matching the global per-Action cron result
// (confirmProposedAssociations tags each ai_slug). `aiSlugs` is the set POSTed.
export function applyOptimisticTag(
  item: HoursLogItem, chosen: string, aiSlugs: Set<string>,
): void {
  let tagged = false;
  for (const a of item.actions ?? []) {
    if (a.ai_slug && aiSlugs.has(a.ai_slug)) {
      a.association_tags = [chosen];
      tagged = true;
    }
  }
  // Only gain `chosen` on the union when an Action of THIS copy was actually
  // tagged — avoids pushing an unrelated week across the multi threshold (which
  // would render a spurious empty split row until the cron corrects it).
  if (tagged && !(item.association_tags ?? []).includes(chosen)) {
    item.association_tags = [...(item.association_tags ?? []), chosen];
  }
}

// The on-screen grouped read-out's hours source: hours for one Item scoped to one
// entity (an association slug) or the Unallocated residual (GROUPED_UNALLOCATED).
// null = no entry data (a read-only, off-roster viewer) → the read-out shows
// narratives only. Routing mirrors the workbook's `_week_bucket_hours`.
// slug → (section → hours). section is an association slug or GROUPED_UNALLOCATED.
export type GroupedHours = Map<string, Map<string, number>>;

// Pure PER-WEEK routing of grid rows to per-(Item, section) hours — the on-screen
// mirror of the workbook's `_week_bucket_hours`, so the editor read-out reconciles
// with the generated .xlsx by construction AND attributes each week's hours the way
// the workbook does. Routing MUST be per-week (not flattened): an Item's
// association can differ week to week (a tag is confirmed in a later run than the
// week the hours were logged), and the workbook routes each week by THAT week's
// tags — so hours logged before the tag existed stay with the week they were
// logged. `perWeek` pairs each week's Items (for that week's tags) with that week's
// rows (summed across members). `known` is the configured entity set (empty = no
// filter). Routing, per week:
//   item:<slug>:<assoc>  → <assoc> when (assoc ∈ that week's Item tags AND known),
//                          else Unallocated (an orphaned/removed-entity split)
//   item:<slug>          → the one tag when the Item has exactly one that week (and
//                          it is known), else Unallocated (multi/untagged base)
// Non-item rows (custom "Also worked") contribute to no per-Item figure here — the
// workbook surfaces them under Unallocated's Also-worked detail lines.
export function routeGroupedHours(
  perWeek: { items: HoursLogItem[]; rows: { row_key: string; hours: number }[] }[],
  known: Set<string>,
): GroupedHours {
  const noFilter = known.size === 0;
  const isKnown = (a: string): boolean => noFilter || known.has(a);
  const buckets: GroupedHours = new Map();
  const add = (slug: string, key: string, h: number): void => {
    if (!h) return;
    const m = buckets.get(slug) ?? new Map<string, number>();
    m.set(key, (m.get(key) ?? 0) + h);
    buckets.set(slug, m);
  };
  for (const wk of perWeek) {
    const tagsBySlug = new Map<string, string[]>();
    for (const it of wk.items) tagsBySlug.set(it.slug, it.association_tags ?? []);
    for (const r of wk.rows) {
      if (!r.row_key.startsWith("item:")) continue;
      const rest = r.row_key.slice(5);
      const ci = rest.indexOf(":");
      const slug = ci < 0 ? rest : rest.slice(0, ci);
      const assoc = ci < 0 ? null : rest.slice(ci + 1);
      const tags = tagsBySlug.get(slug);
      if (!tags) continue;  // a row for an Item not tracked in that week
      if (assoc !== null) {
        add(slug, tags.includes(assoc) && isKnown(assoc) ? assoc : GROUPED_UNALLOCATED, r.hours);
      } else {
        add(slug, tags.length === 1 && isKnown(tags[0]!) ? tags[0]! : GROUPED_UNALLOCATED, r.hours);
      }
    }
  }
  return buckets;
}

// Hours for one Item scoped to one section (an association slug or
// GROUPED_UNALLOCATED), 0 when none.
export function groupedHoursOf(h: GroupedHours, itemSlug: string, key: string): number {
  return h.get(itemSlug)?.get(key) ?? 0;
}

// The base custom key, dropping any `:<assoc>` suffix. Kept so the add-a-row
// counter (mintSharedCustomKey) seeds off the base seq even for a legacy tagged
// key that may still be in stored data (the per-association tagging of custom rows
// is deferred, but an old `custom:…:<assoc>` key could linger).
export function customRowBase(rowKey: string): string {
  if (!rowKey.startsWith("custom:")) return rowKey;
  const rest = rowKey.slice(7);
  const ci = rest.indexOf(":");
  return ci < 0 ? rowKey : `custom:${rest.slice(0, ci)}`;
}

// The grid section (an association slug, or GROUPED_UNALLOCATED) that one hours
// key bills to — the editable grid's mirror of routeGroupedHours / the workbook's
// `_week_bucket_hours`, so a row's cell lands in the SAME section the bill counts
// it in. `tags` is the Item's current association_tags; `known` the CONFIGURED
// entity set (empty = no filter). A base `item:<slug>` → the one tag when the Item
// has exactly one configured tag, else Unallocated; a split `item:<slug>:<X>` → X
// when X is a current configured tag (so a lingering SAME-entity split on a
// single-tag Item lands under that entity, not nowhere), else Unallocated (a stray
// left after X was removed from the tags, or a split to a non-configured entity).
export function gridSectionOfKey(
  slug: string, tags: string[], rowKey: string, known: Set<string>,
): string {
  const isKnown = (a: string): boolean => known.size === 0 || known.has(a);
  const base = `item:${slug}`;
  if (rowKey === base) {
    return tags.length === 1 && isKnown(tags[0]!) ? tags[0]! : GROUPED_UNALLOCATED;
  }
  if (rowKey.startsWith(`${base}:`)) {
    const x = rowKey.slice(base.length + 1);
    return tags.includes(x) && isKnown(x) ? x : GROUPED_UNALLOCATED;
  }
  return GROUPED_UNALLOCATED;  // not this Item's key
}

// The item summary shown when a whole Item's work belongs to one section (a
// single-association Item under its one entity, or a fully-untagged Item under
// Unallocated) — the #109b narrative, else the Action-title roll-up.
function itemSummaryText(it: HoursLogItem): string {
  return it.actions_summary
    ?? (it.actions ?? []).map((a) => a.text).filter(Boolean).join("; ");
}

// The narrative for Item `it` scoped to section `sectionKey` (an association slug
// or GROUPED_UNALLOCATED), action-level — composed from the Item's CURRENT
// (newest-week) Actions:
//   - a SINGLE-association Item under its one entity → its full summary (all its
//     work rolls to that one entity);
//   - a MULTI-association Item under entity X → only X's Actions' text;
//   - Unallocated: a fully-untagged Item → full summary; a multi Item → its
//     untagged-Action residual (the split None row).
function scopedGroupedNarrative(
  it: HoursLogItem, sectionKey: string, order: string[],
  nameFor: (slug: string) => string,
): string {
  const tags = it.association_tags ?? [];
  if (sectionKey === GROUPED_UNALLOCATED) {
    if (tags.length === 0) return itemSummaryText(it);
    // Unallocated residual = Actions with NO configured tag — the untagged ones AND
    // any tagged only to a non-configured association (whose hours also bill here),
    // so that work's text surfaces on screen too (parity with the agent's
    // _scoped_action_text(it, None, known)). `order` is the configured entity set.
    const known = new Set(order);
    return (it.actions ?? [])
      .filter((a) => !(a.association_tags ?? []).some((t) => known.has(t)))
      .map((a) => a.text).filter(Boolean).join("; ");
  }
  if (tags.length <= 1) return itemSummaryText(it);
  const rows = splitItemByAssociation(it, order, nameFor);
  return rows.find((r) => r.assoc === sectionKey)?.narrative ?? "";
}

// An action-scoped read-out card: Item name + the scoped narrative + (when entry
// data is in scope) the scoped hours figure for this section. No whole-Item
// "also under X" flag — the work is partitioned per entity, not duplicated.
function renderGroupedCard(
  it: HoursLogItem, narrative: string, hours: number | null,
): HTMLElement {
  const card = document.createElement("article");
  card.className = "hours-log-item";
  card.dataset.slug = it.slug;
  const title = document.createElement("div");
  title.className = "hours-log-item-title";
  title.textContent = it.name;
  if (hours !== null) {
    const titleRow = document.createElement("div");
    titleRow.className = "hours-log-item-titlerow";
    titleRow.appendChild(title);
    const hrs = document.createElement("span");
    hrs.className = "hours-log-item-billhours";
    hrs.textContent = `${Math.round(hours * 4) / 4} h`;
    titleRow.appendChild(hrs);
    card.appendChild(titleRow);
  } else {
    card.appendChild(title);
  }
  if (narrative) {
    const line = document.createElement("div");
    line.className = "hours-log-item-summary";
    line.textContent = narrative;
    card.appendChild(line);
  }
  return card;
}

// The on-screen Grouped read-out. Read-only for EVERYONE (the fix). Two modes:
//   - EDITOR (`hours` present): membership + figures are DRIVEN BY the per-week
//     routed hours, so the read-out mirrors the billing workbook exactly — an Item
//     appears under a section iff it has logged hours there, with its scoped
//     narrative + that section's hours + a section total. Reconciles with the .xlsx
//     by construction (same `_week_bucket_hours` routing).
//   - READ-ONLY VIEWER (`hours` null, off-roster board member): a narrative preview
//     placed by the Item's current tags (no hours — they do not bill).
function renderGroupedActivity(
  container: HTMLElement, weeks: HoursLogWeek[], snapshot: HoursLogSnapshot,
  data: { hours: GroupedHours; alsoWorked: Map<string, { label: string; hours: number }[]> } | null = null,
): void {
  const hours = data?.hours ?? null;
  const nameOf = new Map(
    (snapshot.associations ?? []).map((a) => [a.slug, a.name] as const),
  );
  const prettySlug = (slug: string): string =>
    slug.replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const nameFor = (slug: string): string => nameOf.get(slug) ?? prettySlug(slug);
  const entityLabel = snapshot.entity_label || "Association";
  // CONFIGURED associations ONLY (no live-tag append) — the bill routes an
  // unconfigured tag to Unallocated, so the read-out must not render a section for
  // it; its hours (routeGroupedHours) and its narrative (scopedGroupedNarrative,
  // via `order`) both land in Unallocated, matching the workbook.
  const order: string[] = (snapshot.associations ?? []).map((a) => a.slug);
  // The Item instance used for a card's narrative = its newest-week copy (weeks are
  // newest-first). Hours are routed per-week; narrative reflects the current state.
  const itemBySlug = new Map<string, HoursLogItem>();
  for (const w of weeks) {
    for (const it of w.items) if (!itemBySlug.has(it.slug)) itemBySlug.set(it.slug, it);
  }

  let rendered = 0;
  const renderSection = (
    key: string, unallocated: boolean, items: HoursLogItem[],
    // Non-Item "Also worked" lines (custom rows) for THIS section — a custom tagged
    // to an association bills under it, an untagged one under Unallocated. Including
    // them keeps each section's on-screen Σ == the billed column. Shown as flat lines.
    extraLines: { label: string; hours: number }[] = [],
  ): void => {
    const shown: HoursLogItem[] = [];
    const narr = new Map<string, string>();
    for (const it of items) {
      const n = scopedGroupedNarrative(it, key, order, nameFor);
      const h = hours ? groupedHoursOf(hours, it.slug, key) : null;
      if (!n && (h ?? 0) <= 0) continue;   // nothing to show for this Item here
      narr.set(it.slug, n);
      shown.push(it);
    }
    if (shown.length === 0 && extraLines.length === 0) return;
    rendered += 1;
    const sec = document.createElement("div");
    sec.className = "hours-log-entity";
    sec.dataset.entity = key;
    const head = document.createElement("h4");
    head.className = "hours-log-entity-head";
    head.textContent = unallocated ? "Unallocated" : `${entityLabel}: ${nameFor(key)}`;
    if (hours) {
      const total = shown.reduce((s, it) => s + groupedHoursOf(hours, it.slug, key), 0)
        + extraLines.reduce((s, l) => s + l.hours, 0);
      const t = document.createElement("span");
      t.className = "hours-log-entity-total";
      t.textContent = `${Math.round(total * 4) / 4} h`;
      head.appendChild(t);
    }
    sec.appendChild(head);
    appendPhaseCategoryGroups(sec, shown, (it) => renderGroupedCard(
      it, narr.get(it.slug) ?? "",
      hours ? groupedHoursOf(hours, it.slug, key) : null,
    ));
    if (extraLines.length > 0) {
      const aw = document.createElement("div");
      aw.className = "hours-log-alsoworked";
      const awHead = document.createElement("div");
      awHead.className = "hours-log-alsoworked-head";
      awHead.textContent = ALSO_WORKED_LABEL;
      aw.appendChild(awHead);
      for (const ln of extraLines) {
        const row = document.createElement("div");
        row.className = "hours-log-entry-row hours-log-bill-row";
        const lbl = document.createElement("span");
        lbl.className = "hours-log-entry-label";
        lbl.textContent = ln.label;
        const hrs = document.createElement("span");
        hrs.className = "hours-log-item-billhours";
        hrs.textContent = `${Math.round(ln.hours * 4) / 4} h`;
        row.appendChild(lbl);
        row.appendChild(hrs);
        aw.appendChild(row);
      }
      sec.appendChild(aw);
    }
    container.appendChild(sec);
  };

  if (hours) {
    // Editor: sections + membership from the per-week routed hours (billing mirror).
    // A tagged custom "Also worked" row shows under its association section; untagged
    // ones under Unallocated.
    const also = data?.alsoWorked ?? new Map<string, { label: string; hours: number }[]>();
    for (const key of [...order, GROUPED_UNALLOCATED]) {
      const unalloc = key === GROUPED_UNALLOCATED;
      const items = [...itemBySlug.keys()]
        .filter((s) => groupedHoursOf(hours, s, key) > 0)
        .map((s) => itemBySlug.get(s)!);
      renderSection(key, unalloc, items, also.get(key) ?? []);
    }
  } else {
    // Read-only viewer: tag-placed narrative preview, no hours.
    for (const group of groupHoursByAssociation(weeks, snapshot)) {
      renderSection(group.key, group.unallocated, group.items);
    }
  }

  if (rendered === 0) {
    const none = document.createElement("p");
    none.className = "hours-log-week-empty";
    none.textContent = "No tracked activity in this period.";
    container.appendChild(none);
  }
}

// HL-6a — the all-staff grid context: every roster member (self + others) with
// its display name + precomputed column initials (stable across weeks). Present
// only in the internal all-staff view (server said can_view_all); null otherwise.
interface GridCtx {
  members: HoursAllStaffMember[];
  initials: string[];
}

// A member's week total (rows + misc buckets). For read-only OTHER columns this
// is computed from the /all payload; the SELF column's live total stays the
// model's (`model.total()`), so unsaved edits reflect immediately.
function memberWeekTotal(w: HoursEntriesMeWeek | null | undefined): number {
  if (!w) return 0;
  const rows = w.rows.reduce((s, r) => s + (r.hours || 0), 0);
  const misc = w.misc ? w.misc.office_admin + w.misc.non_billable + w.misc.pto : 0;
  return rows + misc;
}

// HL-6a — render one week as an all-staff grid: item rows × member columns.
// ONE editable column — the viewer's own (`is_self`), tinted — carries ALL of
// their entry: project items, an "Also worked" add-row, AND the three
// non-project (misc/PTO) buckets. Every teammate column is read-only from the
// /all payload. A per-member Total closes it, and Save sits directly below. This
// keeps all self-entry inside the one column (no disconnected far-right footer)
// and makes each teammate's PTO/admin split visible so the Total reconciles.
// G2 Phase B: when grouped is on, the grid expands a multi-association Item into
// per-entity split sub-rows. The ordered entity list + name map + label come from
// the snapshot; null = ungrouped (the flat grid, one row per Item).
interface GroupedGridCtx {
  order: string[];
  nameFor: (slug: string) => string;
  entityLabel: string;
  // G2 Phase B: the run id for an inline association tag (null → tagging off).
  runId: string | null;
  // Apply an optimistic tag to EVERY occurrence of the Item (by slug) across all
  // weeks in the snapshot, matching the POSTed Action ai_slugs — so a multi-week
  // Item re-groups consistently (the cron correction is global/per-Action). Set
  // by renderHoursLog, where the full snapshot is in scope.
  applyTag: (slug: string, chosen: string, aiSlugs: Set<string>) => void;
}

function buildAllStaffGrid(
  week: HoursLogWeek, items: HoursLogItem[], ui: EntryUI | null, gridCtx: GridCtx,
  placedQuiet: HoursLogNoActivityItem[] = [],
  groupedCtx: GroupedGridCtx | null = null,
  // G2 (fix): lay the editable grid out BY ASSOCIATION (section bands + per-entity
  // split-key rows + Unallocated + the inline Tag) instead of Phase→Category — the
  // Grouped editor surface, so a person enters hours AS an association and they
  // propagate to that association's split key (and the workbook column).
  assocGroup = false,
  // G2 (fix): the Ungrouped all-staff view is a READ-ONLY rollup — every Item shows its
  // derived total (model-sourced, live), no entry cells / add-row / Save. Entry is in
  // the Grouped grid. `editable` still holds (the model is live) so the derived totals
  // reflect unsaved Grouped edits; `cellsEditable` gates the actual entry affordances.
  readOnly = false,
): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "hours-log-grid-wrap";
  const table = document.createElement("table");
  table.className = "hours-log-grid";
  const weekData = gridCtx.members.map(
    (m) => m.weeks.find((w) => w.week_end === week.week_end) ?? null,
  );
  const model = ui ? ui.controller.week(week.week_end) : undefined;
  const editable = Boolean(ui && model);
  // Entry affordances (input cells, add-a-row, Save) are off in a READ-ONLY rollup;
  // the model stays live so derived totals still reflect unsaved Grouped edits.
  const cellsEditable = editable && !readOnly;
  const nCols = gridCtx.members.length + 1;
  const fmt = (h: number | undefined | null): string =>
    h && h > 0 ? String(h) : h === 0 ? "0" : "";
  const otherRowHours = (i: number, rowKey: string): number | undefined =>
    weekData[i]?.rows.find((r) => r.row_key === rowKey)?.hours;
  // Every ACTUAL hours key for one Item this week — the base `item:<slug>` PLUS any
  // per-entity split `item:<slug>:<assoc>` — unioned across members, read from the
  // SAME source the cells read (the live model when editable, else the persisted
  // week). Drives the derived total + the stray-split routing off real keys (not
  // the Item's current tags), so a split left behind after a tag was removed still
  // counts + shows — matching billItemHours / the workbook's `_week_bucket_hours`.
  const itemKeysOf = (slug: string): string[] => {
    const base = `item:${slug}`;
    const isKey = (k: string): boolean => k === base || k.startsWith(`${base}:`);
    const keys = new Set<string>();
    gridCtx.members.forEach((m, i) => {
      const mdl = editable ? modelFor(m) : null;
      const rows = mdl ? mdl.serializeRows() : (weekData[i]?.rows ?? []);
      for (const r of rows) if (isKey(r.row_key)) keys.add(r.row_key);
    });
    return [...keys];
  };

  // HL-6b: the model backing an editable cell for a member's column — the self
  // column always (existing), a teammate column only for an HR viewer. null =
  // read-only for this viewer. (Custom add-rows stay self-only; see below.)
  const canEditAll = Boolean(ui?.controller.canEditAll);
  const modelFor = (m: HoursAllStaffMember): WeekEntryModel | null => {
    if (!ui) return null;
    if (m.is_self) return model ?? null;
    return ui.controller.memberModel(m.id, week.week_end) ?? null;
  };

  const thead = document.createElement("thead");
  const hr = document.createElement("tr");
  const corner = document.createElement("th");
  corner.className = "hg-item-col";
  corner.textContent = "Item";
  hr.appendChild(corner);
  gridCtx.members.forEach((m, i) => {
    const th = document.createElement("th");
    const hrEditable = canEditAll && !m.is_self;
    th.className = m.is_self ? "hg-col hg-self" : hrEditable ? "hg-col hg-hr" : "hg-col";
    th.textContent = gridCtx.initials[i]!;
    th.title = m.is_self ? `${m.name} (you)` : m.name;
    // HL-6c: a column-level ✎ cue — this column has HR-locked cell(s) (per the
    // payload) OR was HR-edited THIS session (the reactive echo). The per-cell 🔒
    // chips carry the detail; the visibility gate (HR / affected employee) is
    // server-side, so a third teammate sees neither.
    const mark = document.createElement("span");
    mark.className = "hg-edited-mark";
    mark.textContent = "✎";
    const paintMark = (): void => {
      const local = ui && !m.is_self
        ? ui.controller.hrEditedMark(m.id, week.week_end) : null;
      const anyLocked = (weekData[i]?.rows ?? []).some((r) => r.locked);
      if (local?.name) {
        mark.title = `Entered by ${local.name}${local.at ? ` · ${isoToShort(local.at)}` : ""}`;
        mark.hidden = false;
      } else if (anyLocked) {
        mark.title = "HR-entered cell(s) in this column";
        mark.hidden = false;
      } else {
        mark.hidden = true;
      }
    };
    paintMark();
    th.appendChild(mark);
    if (ui) ui.refreshers.push(paintMark);
    hr.appendChild(th);
  });
  thead.appendChild(hr);
  table.appendChild(thead);

  const tbody = document.createElement("tbody");
  const band = (label: string, title?: string, cls?: string): void => {
    const bandRow = document.createElement("tr");
    bandRow.className = cls ? `hg-band ${cls}` : "hg-band";
    const cell = document.createElement("td");
    cell.colSpan = nCols;
    cell.textContent = label;
    if (title) cell.title = title;
    bandRow.appendChild(cell);
    tbody.appendChild(bandRow);
  };
  // A row: first cell `nameEl`, then one cell per member. `editCell(m,i,mdl)`
  // builds the editable element for a column whose viewer-model is `mdl` — the
  // self column, or a teammate column when the viewer is HR. Return null to
  // DECLINE (that column falls back to read-only `otherText(i)`) — the self-only
  // add-rows use this so a teammate column stays read text there. A column with
  // no editable model (read-only viewer) always renders `otherText(i)`.
  const memberRow = (
    nameEl: HTMLElement, cls: string,
    editCell:
      | ((m: HoursAllStaffMember, i: number, mdl: WeekEntryModel) => HTMLElement | null)
      | null,
    otherText: (i: number) => string,
  ): HTMLTableRowElement => {
    const tr = document.createElement("tr");
    tr.className = cls;
    const nameCell = document.createElement("td");
    nameCell.className = "hg-item-col";
    nameCell.appendChild(nameEl);
    tr.appendChild(nameCell);
    gridCtx.members.forEach((m, i) => {
      const td = document.createElement("td");
      const mdl = editable ? modelFor(m) : null;
      const node = mdl && editCell ? editCell(m, i, mdl) : null;
      td.className = node
        ? m.is_self ? "hg-cell hg-self" : "hg-cell hg-hr"
        : "hg-cell hg-ro";
      if (node) td.appendChild(node);
      else td.textContent = otherText(i);
      tr.appendChild(td);
    });
    return tr;
  };
  const textNode = (s: string): HTMLElement => {
    const span = document.createElement("span");
    span.textContent = s;
    return span;
  };
  // E: a Phase eyebrow spanning the grid (parity with the card view's phase
  // tier — the grid previously banded by Category only).
  const phaseBand = (labelText: string): void => {
    const bandRow = document.createElement("tr");
    bandRow.className = "hg-phase-band";
    const cell = document.createElement("td");
    cell.colSpan = nCols;
    cell.textContent = labelText;
    bandRow.appendChild(cell);
    tbody.appendChild(bandRow);
  };
  // F: the Item column carries the name + the action-summary line ("what was
  // worked on") — parity with the card view's renderItem, which the grid dropped.
  const itemNameEl = (it: HoursLogItem, summaryOverride?: string): HTMLElement => {
    const wrap = document.createElement("div");
    wrap.className = "hg-item-name";
    const nm = document.createElement("div");
    nm.className = "hg-item-name-main";
    nm.textContent = it.name;
    wrap.appendChild(nm);
    // G2: the association-grouped grid scopes the summary to the entity's Actions;
    // the flat grid uses the full week narrative. `""` override = show no summary.
    const summary = summaryOverride !== undefined
      ? summaryOverride
      : it.actions_summary
        ?? it.actions.map((a) => a.text).filter(Boolean).join("; ");
    if (summary) {
      const s = document.createElement("div");
      s.className = "hg-item-summary";
      s.textContent = summary;
      wrap.appendChild(s);
    }
    return wrap;
  };

  // G2 Phase B — emit a MULTI-association Item as a derived Item-total row (per
  // member = Σ its split rows; live for editable columns so a typed split updates
  // the total at once) + one editable split sub-row per entity (+ Unallocated).
  // A derived (read-only) Item-total row: per member = Σ the given keys; live for
  // editable columns so a typed split updates the total at once. Shared by the
  // grouped split-item total and the flat (Ungrouped) read-only multi-Item row.
  const appendDerivedTotalRow = (it: HoursLogItem, keys: string[]): void => {
    const tr = document.createElement("tr");
    tr.className = "hg-item-row hg-split-total";
    tr.dataset.slug = it.slug;
    const nameTd = document.createElement("td");
    nameTd.className = "hg-item-col";
    nameTd.appendChild(itemNameEl(it));
    tr.appendChild(nameTd);
    gridCtx.members.forEach((m, i) => {
      const td = document.createElement("td");
      const mdl = editable ? modelFor(m) : null;
      td.className = (mdl
        ? m.is_self ? "hg-cell hg-self" : "hg-cell hg-hr"
        : "hg-cell hg-ro") + " hg-split-total-cell";
      const paint = (): void => {
        const sum = mdl
          ? keys.reduce((s, k) => s + (mdl.hasEntry(k) ? mdl.getHours(k) : 0), 0)
          : keys.reduce((s, k) => s + (otherRowHours(i, k) ?? 0), 0);
        td.textContent = sum > 0 ? String(Math.round(sum * 4) / 4) : "";
      };
      paint();
      if (mdl && ui) ui.refreshers.push(paint);
      tr.appendChild(td);
    });
    tbody.appendChild(tr);
  };

  // G2 (fix): a per-member read-only subtotal row for one association section =
  // Σ the section's row-keys per member (live, so a typed cell updates it at once).
  const appendSubtotalRow = (label: string, keys: string[]): void => {
    const tr = document.createElement("tr");
    tr.className = "hg-item-row hg-assoc-subtotal";
    const nameTd = document.createElement("td");
    nameTd.className = "hg-item-col";
    nameTd.appendChild(textNode(label));
    tr.appendChild(nameTd);
    gridCtx.members.forEach((m, i) => {
      const td = document.createElement("td");
      const mdl = editable ? modelFor(m) : null;
      td.className = "hg-cell hg-ro hg-split-total-cell";
      const paint = (): void => {
        const sum = mdl
          ? keys.reduce((s, k) => s + (mdl.hasEntry(k) ? mdl.getHours(k) : 0), 0)
          : keys.reduce((s, k) => s + (otherRowHours(i, k) ?? 0), 0);
        td.textContent = sum > 0 ? String(Math.round(sum * 4) / 4) : "";
      };
      paint();
      if (mdl && ui) ui.refreshers.push(paint);
      tr.appendChild(td);
    });
    tbody.appendChild(tr);
  };

  // G2 Phase B — inline tagging on the None row. The Item's UNTAGGED Actions are
  // the work not yet assigned to an entity; tagging them with a chosen
  // association (action_set_associations per Action — the SAME correction the
  // briefing picker uses) moves that work to a new entity row. Optimistic: the
  // untagged Actions + the Item's union are mutated in place, then repaint.
  // No-op in fixture mode (confirmProposedAssociations doesn't POST there).
  const tagOptions = (it: HoursLogItem): { slug: string; name: string }[] =>
    groupedCtx
      ? inlineTagOptions(it, groupedCtx.order).map((s) => ({ slug: s, name: groupedCtx.nameFor(s) }))
      : [];
  const canInlineTag = (it: HoursLogItem): boolean =>
    Boolean(ui) && !!groupedCtx
    && inlineTagTargets(it).length > 0 && tagOptions(it).length > 0;
  const applyInlineTag = async (it: HoursLogItem, chosen: string): Promise<void> => {
    const targets = inlineTagTargets(it);
    const aiSlugs = new Set(targets.map((a) => a.ai_slug).filter(Boolean) as string[]);
    if (it.thread_slug && groupedCtx?.runId) {
      for (const a of targets) {
        await confirmProposedAssociations(
          groupedCtx.runId, actionItemKey(it.thread_slug, it.slug, a.ai_slug!), [chosen],
        );
      }
    }
    // Replay those exact ai_slugs across EVERY week's copy of this Item (not a
    // per-week re-derivation) so the optimistic re-group matches the global
    // per-Action cron correction.
    if (groupedCtx) groupedCtx.applyTag(it.slug, chosen, aiSlugs);
    else applyOptimisticTag(it, chosen, aiSlugs);
    ui?.repaint();
  };
  const openTagPicker = (anchor: HTMLElement, it: HoursLogItem): void => {
    document.querySelectorAll(".hg-tag-pop").forEach((p) => p.remove());
    const pop = document.createElement("div");
    pop.className = "assoc-confpop hg-tag-pop";
    const title = document.createElement("p");
    title.className = "cp-t";
    title.textContent =
      `Tag this work to ${groupedCtx?.entityLabel ?? "Association"}:`;
    pop.appendChild(title);
    const list = document.createElement("div");
    list.className = "cp-opts";
    pop.appendChild(list);
    const close = (): void => {
      pop.remove();
      document.removeEventListener("mousedown", onDoc);
    };
    const onDoc = (e: MouseEvent): void => {
      if (!pop.contains(e.target as Node) && e.target !== anchor) close();
    };
    for (const o of tagOptions(it)) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "cp-opt";
      b.appendChild(renderAssocTagChip(o.slug));
      const lbl = document.createElement("span");
      lbl.className = "cp-opt-label";
      lbl.textContent = o.name;
      b.appendChild(lbl);
      b.addEventListener("click", async () => {
        list.querySelectorAll("button").forEach(
          (x) => ((x as HTMLButtonElement).disabled = true),
        );
        try { await applyInlineTag(it, o.slug); close(); }
        catch {
          list.querySelectorAll("button").forEach(
            (x) => ((x as HTMLButtonElement).disabled = false),
          );
        }
      });
      list.appendChild(b);
    }
    const r = anchor.getBoundingClientRect();
    pop.style.position = "fixed";
    pop.style.left = `${Math.round(r.left)}px`;
    pop.style.top = `${Math.round(r.bottom + 4)}px`;
    document.body.appendChild(pop);
    setTimeout(() => document.addEventListener("mousedown", onDoc), 0);
  };

  // --- Custom "Also worked" rows (bill to Unallocated). --------------------------
  // The configured entity set, used by the by-association ITEM layout (knownAssoc /
  // isKnownAssoc gate the scaffold + gridSectionOfKey routing).
  const knownAssoc = new Set(groupedCtx?.order ?? []);
  const isKnownAssoc = (a: string): boolean => knownAssoc.size === 0 || knownAssoc.has(a);
  // Shared custom rows (rowKey → label across members), collected from the editable
  // models, shown in the "Also worked" band (they bill to Unallocated).
  const sharedCustomAll = new Map<string, string>();
  if (editable) {
    gridCtx.members.forEach((m) => {
      const mdl = modelFor(m);
      if (!mdl) return;
      for (const { rowKey, label } of mdl.customRows()) {
        if (!sharedCustomAll.has(rowKey)) sharedCustomAll.set(rowKey, label);
      }
    });
  }
  // One shared custom row: an editable label (shared rename) + a delete + a per-member
  // hours box. Custom "Also worked" rows bill to Unallocated (the per-association
  // tagging of ad-hoc rows was deferred — its shared-key model needs a redesign). In a
  // READ-ONLY rollup it collapses to a label + per-member model-sourced hours (no edit).
  const customRowEl = (rowKey: string, label: string): HTMLTableRowElement => {
    if (!cellsEditable) {
      return memberRow(
        textNode(label), "hg-item-row hg-also-row", null,
        (i) => {
          const mdl = editable ? modelFor(gridCtx.members[i]!) : null;
          const h = mdl ? (mdl.hasEntry(rowKey) ? mdl.getHours(rowKey) : 0)
            : (otherRowHours(i, rowKey) ?? 0);
          return h > 0 ? String(Math.round(h * 4) / 4) : "";
        },
      );
    }
    const lbl = document.createElement("input");
    lbl.className = "hours-log-custom-label";
    lbl.type = "text";
    lbl.value = label;
    lbl.setAttribute("aria-label", "Row description");
    const labelOf = (): string => lbl.value.trim() || label;
    lbl.addEventListener("input", () => {
      const next = labelOf();
      gridCtx.members.forEach((m) => modelFor(m)?.setLabel(rowKey, next));
      ui!.refreshAll();
    });
    const labelWrap = document.createElement("div");
    labelWrap.className = "hg-custom-label-wrap";
    labelWrap.appendChild(lbl);
    labelWrap.appendChild(makeRowDeleteButton(() => {
      gridCtx.members.forEach((m) => modelFor(m)?.clearHours(rowKey));
      ui!.repaint();
    }));
    return memberRow(
      labelWrap, "hg-item-row hg-also-row",
      (m, _i, mdl) => makeHoursCell(mdl, rowKey, labelOf, ui!, { hrEditable: !m.is_self }),
      (i) => fmt(otherRowHours(i, rowKey)),
    );
  };

  // --- Project items. The EDITABLE Grouped surface lays them out BY ASSOCIATION
  // (section bands + per-entity split-key rows + Unallocated + the inline Tag), so a
  // person enters hours AS an association and they write that association's split key
  // (and land in the matching workbook column). Otherwise the flat Phase → Category
  // grid (Ungrouped), where a multi-association Item is read-only (split in Grouped).
  if (assocGroup && groupedCtx) {
    const { order, nameFor, entityLabel } = groupedCtx;
    const sortedA = [...items].sort(
      (a, b) =>
        phaseSortKey(a.phase) - phaseSortKey(b.phase) ||
        (a.thread_name ?? "").localeCompare(b.thread_name ?? ""),
    );
    interface ARow { it: HoursLogItem; rowKey: string; summary: string; tag: boolean }
    const sectionRows = new Map<string, ARow[]>();
    const addRow = (key: string, r: ARow): void => {
      const arr = sectionRows.get(key) ?? [];
      arr.push(r); sectionRows.set(key, arr);
    };
    // Lay each Item out by the SAME per-key routing the bill uses (gridSectionOfKey
    // / routeGroupedHours / the workbook's _week_bucket_hours), over the CONFIGURED
    // entities only (`knownAssoc` = groupedCtx.order, the config set):
    //   - an entry SCAFFOLD so every configured entity has an editable row even at
    //     0h — the base key under the Item's one configured entity (single tag) else
    //     under Unallocated; one split row per configured tag of a multi-entity Item;
    //   - then every ACTUAL key the scaffold did not place, routed to its bill
    //     section: a lingering same-entity split (item:<slug>:<tag>) under that
    //     entity, a stray / unconfigured split under Unallocated.
    // So each section's on-screen Σ == the bill (no hidden/uneditable hours).
    for (const it of sortedA) {
      const tags = it.association_tags ?? [];
      const base = `item:${it.slug}`;
      const placed = new Set<string>();
      // base key — bill routing: one configured tag → that entity, else Unallocated.
      const baseSection = tags.length === 1 && isKnownAssoc(tags[0]!)
        ? tags[0]! : GROUPED_UNALLOCATED;
      addRow(baseSection, {
        it, rowKey: base,
        summary: scopedGroupedNarrative(it, baseSection, order, nameFor),
        tag: baseSection === GROUPED_UNALLOCATED,
      });
      placed.add(base);
      // split ENTRY rows per configured tag not already covered by the base (a
      // multi-entity Item splits per entity; a single-entity Item's base IS its entry).
      for (const t of tags) {
        if (!isKnownAssoc(t) || t === baseSection) continue;
        const rowKey = `${base}:${t}`;
        if (placed.has(rowKey)) continue;
        addRow(t, { it, rowKey, summary: scopedGroupedNarrative(it, t, order, nameFor), tag: false });
        placed.add(rowKey);
      }
      // every remaining ACTUAL key → its bill section (a same-entity split lands under
      // its entity; a stray / unconfigured split under Unallocated) — visible,
      // editable, in the subtotal, matching the bill.
      for (const key of itemKeysOf(it.slug)) {
        if (placed.has(key)) continue;
        addRow(gridSectionOfKey(it.slug, tags, key, knownAssoc), { it, rowKey: key, summary: "", tag: false });
        placed.add(key);
      }
    }
    // Custom "Also worked" rows bill to Unallocated and render in the "Also worked"
    // band below — they are NOT placed under association sections (the per-association
    // tagging of ad-hoc rows was deferred).
    for (const key of [...order, GROUPED_UNALLOCATED]) {
      const rows = sectionRows.get(key) ?? [];
      if (rows.length === 0) continue;
      const sectionName = key === GROUPED_UNALLOCATED ? "Unallocated" : nameFor(key);
      band(
        key === GROUPED_UNALLOCATED ? "Unallocated" : `${entityLabel}: ${sectionName}`,
        undefined, "hg-assoc-band",
      );
      for (const r of rows) {
        const nameEl = itemNameEl(r.it, r.summary);
        if (r.tag && canInlineTag(r.it)) {
          const tagBtn = document.createElement("button");
          tagBtn.type = "button";
          tagBtn.className = "hg-tag-btn";
          tagBtn.textContent = "Tag…";
          tagBtn.title = "Assign this untagged work to an association";
          tagBtn.addEventListener("click", () => openTagPicker(tagBtn, r.it));
          nameEl.appendChild(tagBtn);
        }
        const row = memberRow(
          nameEl, "hg-item-row",
          editable
            ? (m, _i, mdl) => makeHoursCell(
                // STORE the plain Item name (the billing fallback for an orphaned
                // key must not carry a section suffix — "Pool — Master" billed under
                // Unallocated misnames the line); the section is aria/display only.
                mdl, r.rowKey, r.it.name, ui!,
                { hrEditable: !m.is_self, ariaLabel: `${r.it.name} — ${sectionName}` },
              )
            : null,
          (i) => fmt(otherRowHours(i, r.rowKey)),
        );
        row.dataset.slug = r.it.slug;
        tbody.appendChild(row);
      }
      appendSubtotalRow(`${sectionName} subtotal`, rows.map((r) => r.rowKey));
    }
  } else {
    const sorted = [...items].sort(
      (a, b) =>
        phaseSortKey(a.phase) - phaseSortKey(b.phase) ||
        (a.thread_name ?? "").localeCompare(b.thread_name ?? ""),
    );
    const showPhases = items.some((it) => it.phase && it.phase !== "general");
    let lastPhase: string | null | undefined = undefined;
    let lastCat: string | null = null;
    for (const it of sorted) {
      if (showPhases && it.phase !== lastPhase) {
        phaseBand(PHASE_LABELS[it.phase ?? ""] ?? "General");
        lastPhase = it.phase;
        lastCat = null;  // restart category grouping inside the new phase
      }
      const cat = it.thread_name ?? "Uncategorized";
      if (cat !== lastCat) { band(cat); lastCat = cat; }
      // READ-ONLY rollup (the all-staff Ungrouped), OR a split-aware multi-Item: show
      // the derived total (= base/unallocated + Σ per-entity splits), model-sourced +
      // live. Entry happens in the Grouped view.
      if (readOnly) {
        // A READ-ONLY rollup shows each Item's derived total (base + Σ actual
        // splits), model-sourced + live. Entry happens in the Grouped grid.
        appendDerivedTotalRow(it, itemKeysOf(it.slug));
        continue;
      }
      const rowKey = `item:${it.slug}`;
      const row = memberRow(
        itemNameEl(it), "hg-item-row",
        cellsEditable
          ? (m, _i, mdl) => makeHoursCell(mdl, rowKey, it.name, ui!, { hrEditable: !m.is_self })
          : null,
        (i) => fmt(otherRowHours(i, rowKey)),
      );
      row.dataset.slug = it.slug;
      tbody.appendChild(row);
    }
  }

  // --- "Also worked this week" — placed §B items + custom add-rows. Always
  // shown in entry mode (the add-a-row is available even when nothing is logged
  // under it yet). HL-6b (A): an HR viewer can add a custom row for ANY employee
  // and edit any teammate's custom rows — the add-a-row exposes an hours box in
  // every editable column, and each committed custom row is editable in its
  // owner's column.
  if (editable && model) {
    // Custom "Also worked" rows are SHARED across the team (one key + label, a
    // per-person hours box); they all bill to Unallocated. In a READ-ONLY rollup,
    // only those that actually carry hours.
    const alsoCustoms = [...sharedCustomAll].filter(([rowKey]) => {
      if (!cellsEditable) {
        return gridCtx.members.some((m) => {
          const mdl = modelFor(m);
          return Boolean(mdl && mdl.hasEntry(rowKey) && mdl.getHours(rowKey) > 0);
        });
      }
      return true;
    });
    // A placed §B "open item" carries hours on its base key `item:<slug>`; in a
    // READ-ONLY rollup show only those that actually carry hours this week (model-
    // sourced, like the custom rows), so the Ungrouped total isn't below the bill
    // (the workbook counts them under Unallocated / Also worked).
    const placedReadOnly = cellsEditable ? [] : placedQuiet.filter((item) => {
      const rowKey = `item:${item.slug}`;
      return gridCtx.members.some((m, i) => {
        const mdl = editable ? modelFor(m) : null;
        const h = mdl ? (mdl.hasEntry(rowKey) ? mdl.getHours(rowKey) : 0)
          : (otherRowHours(i, rowKey) ?? 0);
        return h > 0;
      });
    });
    if (cellsEditable || alsoCustoms.length > 0 || placedReadOnly.length > 0) {
      band(ALSO_WORKED_LABEL);
      const placedLabel = (item: HoursLogNoActivityItem): HTMLElement => {
        const label = textNode(item.name);
        const tag = document.createElement("span");
        tag.className = "hours-log-placed-tag";
        tag.textContent = "open item";
        label.appendChild(tag);
        return label;
      };
      if (cellsEditable) {
        for (const item of placedQuiet) {
          const rowKey = `item:${item.slug}`;
          tbody.appendChild(memberRow(
            placedLabel(item), "hg-item-row hg-also-row",
            (m, _i, mdl) => makeHoursCell(mdl, rowKey, item.name, ui!, {
              onEmptyBlur: () => ui!.repaint(), hrEditable: !m.is_self,
            }),
            (i) => fmt(otherRowHours(i, rowKey)),
          ));
        }
      } else {
        for (const item of placedReadOnly) {
          const rowKey = `item:${item.slug}`;
          tbody.appendChild(memberRow(
            placedLabel(item), "hg-item-row hg-also-row", null,
            (i) => {
              const mdl = editable ? modelFor(gridCtx.members[i]!) : null;
              const h = mdl ? (mdl.hasEntry(rowKey) ? mdl.getHours(rowKey) : 0)
                : (otherRowHours(i, rowKey) ?? 0);
              return h > 0 ? String(Math.round(h * 4) / 4) : "";
            },
          ));
        }
      }
      for (const [rowKey, label] of alsoCustoms) {
        tbody.appendChild(customRowEl(rowKey, label));
      }
    }
    // Add-a-row: a shared label + an hours box in every editable column.
    // Committing hours under a member mints a fresh SHARED key and adds the row
    // to that member; it then materializes as a shared row with a box in every
    // column (so hours can be added for anyone). Repaint rebuilds the add-row.
    // HL-6d: NO add-a-row on a frozen (billed) month — you can't add new
    // billable work to a signed-off period (committed "Also worked" rows above
    // are already frozen read-only via makeHoursCell; the Worker 403s an add
    // regardless). HR unfreezes the month to add. Also off in a read-only rollup.
    if (cellsEditable && !ui!.frozenMonths.has(billingMonth(week.week_end))) {
      const addLabel = document.createElement("input");
      addLabel.className = "hours-log-add-label";
      addLabel.type = "text";
      addLabel.placeholder = canEditAll
        ? "Add a row not listed — then enter hours in each person's column"
        : "Worked on something not listed? Add a project row";
      addLabel.setAttribute("aria-label", "New project row description");
      const addRow = document.createElement("tr");
      addRow.className = "hg-add-row";
      const addNameCell = document.createElement("td");
      addNameCell.className = "hg-item-col";
      addNameCell.appendChild(addLabel);
      addRow.appendChild(addNameCell);
      gridCtx.members.forEach((m) => {
        const td = document.createElement("td");
        const mdl = editable ? modelFor(m) : null;
        td.className = mdl
          ? m.is_self ? "hg-cell hg-self" : "hg-cell hg-hr"
          : "hg-cell hg-ro";
        if (mdl) {
          const addHours = document.createElement("input");
          addHours.className = "hours-log-cell";
          addHours.type = "text";
          addHours.inputMode = "decimal";
          addHours.setAttribute(
            "aria-label", `New project row hours for ${m.name}`,
          );
          addHours.addEventListener("change", () => {
            const label = addLabel.value.trim();
            const raw = addHours.value.trim();
            const n = parseFloat(raw);
            if (!label || raw === "" || !isFinite(n) || n < 0) return;
            const key = ui!.controller.mintSharedCustomKey(week.week_end);
            mdl.setHours(key, label, normalizeHours(raw));
            ui!.repaint();
          });
          td.appendChild(addHours);
        }
        addRow.appendChild(td);
      });
      tbody.appendChild(addRow);
    }
  }

  // --- Non-project time (misc buckets) — self editable, teammates read-only. --
  // Read-only teammate misc: blank a 0 (unlike an item cell, a 0 in a PTO/admin
  // bucket carries no "addressed, no time" meaning — it's just noise).
  const otherMisc = (i: number, bucket: keyof MiscBuckets): string => {
    // In a read-only rollup, source from the live model (reflects unsaved Grouped
    // edits); a read-only teammate column otherwise uses the persisted payload.
    const mdl = readOnly && editable ? modelFor(gridCtx.members[i]!) : null;
    const v = mdl ? mdl.misc[bucket] : (weekData[i]?.misc?.[bucket] ?? 0);
    return v > 0 ? String(v) : "";
  };
  const miscTotal = (i: number): number => {
    const mdl = readOnly && editable ? modelFor(gridCtx.members[i]!) : null;
    const mi = mdl ? mdl.misc : weekData[i]?.misc;
    return mi ? mi.office_admin + mi.non_billable + mi.pto : 0;
  };
  // Show misc when entry is on (add it), or when any member actually logged some.
  const anyMisc = cellsEditable || gridCtx.members.some((_m, i) => miscTotal(i) > 0);
  if (anyMisc) {
    // B4: HR may fill a teammate's misc/PTO too — but misc is employee-week and
    // project-agnostic, so the tooltip warns an edit spans ALL that person's
    // projects (the Worker writes the shared hours-misc/<email>/<week> doc).
    band(
      "Non-project time",
      canEditAll
        ? "PTO / Office / Non-billable are per-person for the whole week — an "
          + "HR edit here applies across ALL that person's projects."
        : undefined,
    );
    for (const bucket of MISC_BUCKETS) {
      tbody.appendChild(memberRow(
        textNode(MISC_LABELS[bucket]), "hg-misc-row",
        cellsEditable ? (m, _i, mdl) => makeMiscCell(mdl, bucket, ui!, { hrEditable: !m.is_self }) : null,
        (i) => otherMisc(i, bucket),
      ));
    }
  }

  // --- Per-member Total. Self is live (model.total()); others from /all. -------
  const totalRow = document.createElement("tr");
  totalRow.className = "hg-total-row";
  const tl = document.createElement("td");
  tl.className = "hg-item-col";
  tl.textContent = "Total";
  totalRow.appendChild(tl);
  gridCtx.members.forEach((m, i) => {
    const td = document.createElement("td");
    const mdl = editable ? modelFor(m) : null;
    td.className = mdl
      ? m.is_self ? "hg-cell hg-self hg-total" : "hg-cell hg-hr hg-total"
      : "hg-cell hg-ro hg-total";
    if (mdl) {
      // Live total from the working copy (self OR an HR-edited teammate) so
      // unsaved edits reconcile immediately; read-only columns use the payload.
      const paint = (): void => { td.textContent = String(mdl.total()); };
      paint();
      ui!.refreshers.push(paint);
    } else {
      td.textContent = String(memberWeekTotal(weekData[i]));
    }
    totalRow.appendChild(td);
  });
  tbody.appendChild(totalRow);
  table.appendChild(tbody);
  wrap.appendChild(table);

  // Save sits directly under the one editable column's grid.
  if (cellsEditable && model) wrap.appendChild(buildSaveBar(week.week_end, ui!, model));
  return wrap;
}

function renderWeek(
  week: HoursLogWeek, ui: EntryUI | null = null,
  placedQuiet: HoursLogNoActivityItem[] = [],
  billing = false,
  gridCtx: GridCtx | null = null,
  groupedCtx: GroupedGridCtx | null = null,
  // G2 (fix): lay the all-staff grid out by association (the editable Grouped surface).
  assocGroup = false,
  // G2 (fix): the Ungrouped all-staff view is a READ-ONLY rollup (entry is in Grouped).
  readOnly = false,
): HTMLElement {
  const block = document.createElement("section");
  block.className = "hours-log-week";

  const h = document.createElement("h4");
  h.className = "hours-log-week-heading";
  h.textContent = fmtWeekRange(week.week_start, week.week_end);
  block.appendChild(h);

  const model = ui ? ui.controller.week(week.week_end) : undefined;
  // HL-5 + D — billing preview = the CLIENT bill, ALL-STAFF billable. Sum each
  // item's hours across every roster member (from the /all payload, gridCtx);
  // misc/PTO suppressed (never on a client bill). For a flat project with no
  // all-staff context, fall back to the viewer's own column. Mirrors the agent's
  // all-staff `billing=` render (which the generated .xlsx already uses).
  const allStaff = gridCtx?.members ?? null;
  const memberWeekOf = (m: HoursAllStaffMember): HoursEntriesMeWeek | undefined =>
    m.weeks.find((w) => w.week_end === week.week_end);
  // A member's LIVE rows: the working copy where one exists (self always; a
  // teammate once HR has touched it) so the bill reflects in-session edits + adds
  // immediately, falling back to the persisted /all payload otherwise. This is
  // why a change or a new "Also worked" row shows on Billing without a reload.
  const liveRowsFor = (m: HoursAllStaffMember): HoursEntryRowInput[] => {
    const mdl = ui
      ? m.is_self
        ? ui.controller.week(week.week_end)
        : ui.controller.peekMemberModel(m.id, week.week_end)
      : undefined;
    return mdl ? mdl.serializeRows() : memberWeekOf(m)?.rows ?? [];
  };
  // G2 Phase B: an Item's billable hours = the base key item:<slug> PLUS every
  // per-entity split item:<slug>:<assoc> (mirrors the agent export's _item_rollup
  // + the internal grid's derived total). Summing the base only undercounts a
  // multi-association Item on the client bill.
  const rowIsItem = (rowKey: string, slug: string): boolean => {
    const base = `item:${slug}`;
    return rowKey === base || rowKey.startsWith(`${base}:`);
  };
  const billItemHours = (slug: string): number => {
    let t = 0;
    if (allStaff) {
      for (const m of allStaff) {
        for (const r of liveRowsFor(m)) if (rowIsItem(r.row_key, slug)) t += r.hours ?? 0;
      }
    } else if (model) {
      for (const r of model.serializeRows()) if (rowIsItem(r.row_key, slug)) t += r.hours ?? 0;
    }
    return Math.round(t * 4) / 4;
  };
  const items = billing
    ? week.items.filter((it) => billItemHours(it.slug) > 0)
    : week.items;
  // HL-6a: the internal all-staff grid (gridCtx present, non-billing) owns the
  // WHOLE week — items + "Also worked" + misc/PTO + Save — all inside the one
  // editable (self) column. The card layout + separate footer is the other path
  // (flat/gated-off internal, and every billing preview).
  const gridMode = Boolean(gridCtx && !billing);
  // Billing all-staff: a week with no §A items can still have billable
  // "Also worked" rows (custom / §B) across members — don't call it empty.
  const billHasAlsoWorked = billing && !!allStaff
    && allStaff.some((m) => liveRowsFor(m).some((r) => (r.hours ?? 0) > 0));
  if (items.length === 0 && placedQuiet.length === 0 && !billHasAlsoWorked) {
    if (gridMode) {
      // A quiet week still shows the grid so misc/PTO + Save stay in-column.
      block.appendChild(
        buildAllStaffGrid(week, items, ui, gridCtx!, placedQuiet, groupedCtx, assocGroup, readOnly),
      );
      return block;
    }
    const none = document.createElement("p");
    none.className = "hours-log-week-empty";
    none.textContent = billing
      ? "No billable hours this week." : "No tracked activity this week.";
    block.appendChild(none);
    // A week with no §A items can still carry misc/PTO + add-a-row in entry mode
    // (internal only — billing never shows the misc footer).
    if (ui && !billing) {
      const extras = renderProjectExtras(week.week_end, ui, placedQuiet);
      if (extras) block.appendChild(extras);
      block.appendChild(renderEntryFooter(week.week_end, ui));
    }
    return block;
  }
  if (gridMode) {
    block.appendChild(
      buildAllStaffGrid(week, items, ui, gridCtx!, placedQuiet, groupedCtx, assocGroup, readOnly),
    );
    return block;
  }
  // D: the all-staff billing bill is READ-ONLY (summed hours per item); the flat
  // (single-logger) billing + the internal card path keep the editable self cell.
  const billAllStaff = billing && !!allStaff;
  appendPhaseCategoryGroups(
    block, items,
    (it) => billAllStaff
      ? renderItem(it, null, billItemHours(it.slug))
      : renderItem(it, ui && model ? { ui, model } : null),
  );
  if (billAllStaff) {
    // All-staff "Also worked" billable lines (§B items + custom rows across every
    // member — anything logged that isn't a §A item), read-only, then the
    // all-staff billable week total (rows only; misc/PTO never on a client bill).
    // A §A item row is the base item:<slug> OR any of its per-entity split keys
    // item:<slug>:<assoc> (G2). Those are already summed by billItemHours above,
    // so they must NOT also appear as "Also worked" lines (that would double-list).
    const itemSlugs = new Set(week.items.map((it) => it.slug));
    const isSectionAItem = (rk: string): boolean =>
      rk.startsWith("item:") && itemSlugs.has(rk.slice(5).split(":")[0] ?? "");
    // Combine "Also worked" lines the way the §A item rows combine — a client
    // bill shows ONE line per piece of work with the hours SUMMED across every
    // employee (a shared row logged for 3 people is "…: 3 h", not three 1 h
    // lines). Group by the row's description; insertion order is preserved.
    const byLabel = new Map<string, number>();
    let weekTotal = 0;
    for (const m of allStaff!) {
      for (const r of liveRowsFor(m)) {
        const hrs = r.hours ?? 0;
        if (hrs <= 0) continue;
        weekTotal += hrs;
        if (!isSectionAItem(r.row_key)) {
          const key = r.label.trim() || r.label;
          byLabel.set(key, (byLabel.get(key) ?? 0) + hrs);
        }
      }
    }
    const lines = [...byLabel.entries()].map(([label, hours]) => ({
      label, hours: Math.round(hours * 4) / 4,
    }));
    if (lines.length) {
      const head = document.createElement("div");
      head.className = "hours-log-alsoworked-head";
      head.textContent = ALSO_WORKED_LABEL;
      block.appendChild(head);
      for (const ln of lines) {
        const row = document.createElement("div");
        row.className = "hours-log-entry-row hours-log-bill-row";
        const lbl = document.createElement("span");
        lbl.className = "hours-log-entry-label";
        lbl.textContent = ln.label;
        const hrs = document.createElement("span");
        hrs.className = "hours-log-item-billhours";
        hrs.textContent = `${Math.round(ln.hours * 4) / 4} h`;
        row.appendChild(lbl);
        row.appendChild(hrs);
        block.appendChild(row);
      }
    }
    const foot = document.createElement("div");
    foot.className = "hours-log-billing-total";
    foot.textContent = `Week total: ${Math.round(weekTotal * 4) / 4} h`;
    block.appendChild(foot);
  } else if (ui) {
    // Project-flow rows (operator #2): items you logged from the open-items
    // list (placedQuiet) + custom add-a-rows, at category indent; then the
    // non-project (misc) footer + Save + total below. Billing preview (flat): the
    // misc/PTO footer is dropped; "Also worked" shows only if it has billable
    // rows; a billable-only Week total replaces the Save footer.
    const extras = renderProjectExtras(week.week_end, ui, placedQuiet, billing);
    if (extras) block.appendChild(extras);
    if (!billing) {
      block.appendChild(renderEntryFooter(week.week_end, ui));
    } else if (model) {
      const foot = document.createElement("div");
      foot.className = "hours-log-billing-total";
      foot.textContent = `Week total: ${model.billableTotal()} h`;
      block.appendChild(foot);
    }
  }
  return block;
}

interface ItemEntryCtx { ui: EntryUI; model: WeekEntryModel }

function renderItem(
  item: HoursLogItem, entry: ItemEntryCtx | null = null,
  readOnlyHours?: number,
): HTMLElement {
  const card = document.createElement("article");
  card.className = "hours-log-item";
  card.dataset.slug = item.slug;

  // #89: Item name only (Category/Phase are grouping headers above) — no owner
  // column (a #91 concern). Below it, ONE condensed line: the action titles
  // joined by "; " (the timesheet roll-up, not a per-action breakout).
  // §7 Phase-2: in entry mode the title row gains a right-aligned hours cell.
  // D (billing all-staff): a read-only summed-hours figure for a client bill.
  const title = document.createElement("div");
  title.className = "hours-log-item-title";
  title.textContent = item.name;
  if (entry) {
    card.classList.add("hours-log-item-entry");
    const titleRow = document.createElement("div");
    titleRow.className = "hours-log-item-titlerow";
    titleRow.appendChild(title);
    titleRow.appendChild(
      makeHoursCell(entry.model, `item:${item.slug}`, item.name, entry.ui),
    );
    card.appendChild(titleRow);
  } else if (readOnlyHours !== undefined) {
    const titleRow = document.createElement("div");
    titleRow.className = "hours-log-item-titlerow";
    titleRow.appendChild(title);
    const hrs = document.createElement("span");
    hrs.className = "hours-log-item-billhours";
    hrs.textContent = `${readOnlyHours} h`;
    titleRow.appendChild(hrs);
    card.appendChild(titleRow);
  } else {
    card.appendChild(title);
  }

  const summary =
    item.actions_summary ??
    item.actions.map((a) => a.text).filter(Boolean).join("; ");
  if (summary) {
    const line = document.createElement("div");
    line.className = "hours-log-item-summary";
    line.textContent = summary;
    card.appendChild(line);
  }

  return card;
}

// §B — known-but-no-activity, rendered into an existing container (re-run on
// filter change so the per-row week dropdown offers the weeks currently in
// view). `inViewWeeks` are the weeks §A is showing.
function renderNoActivityInto(
  section: HTMLElement,
  items: HoursLogNoActivityItem[],
  ui: EntryUI | null,
  inViewWeeks: HoursLogWeek[],
): void {
  const h = document.createElement("h3");
  h.className = "hours-log-section-heading";
  h.textContent = "Open, but no recent activity";
  section.appendChild(h);

  const note = document.createElement("p");
  note.className = "hours-log-subtitle";
  note.textContent = ui
    ? (inViewWeeks.length > 1
        ? "Worked one of these off the record? Enter hours and pick the week — "
          + "it moves up into that week."
        : "Worked one of these off the record? Enter hours — it moves up into "
          + "the week above.")
    : "Still open but with nothing captured recently — did you work on any of "
      + "these off the record?";
  section.appendChild(note);

  const entry: QuietEntryCtx | null = ui ? { ui, weeks: inViewWeeks } : null;
  // (e) Same Phase → Category grouping as the main section.
  appendPhaseCategoryGroups(section, items, (it) => renderQuietItem(it, entry));

  // §B Save flushes EVERY dirty week (rows can target any in-view week now).
  if (ui) {
    const bar = document.createElement("div");
    bar.className = "hours-log-save-bar hours-log-noactivity-save";
    const saveBtn = document.createElement("button");
    saveBtn.type = "button";
    saveBtn.className = "hours-log-save-btn";
    saveBtn.textContent = "Save";
    const err = document.createElement("span");
    err.className = "hours-log-save-error";
    err.hidden = true;
    saveBtn.addEventListener("click", async () => {
      saveBtn.disabled = true;
      saveBtn.textContent = "Saving…";
      err.hidden = true;
      try {
        await ui.controller.saveAllDirty();
        saveBtn.textContent = "Saved";
        ui.refreshAll();  // clean → the refresher disables it
      } catch (e) {
        err.hidden = false;
        err.textContent = e instanceof Error ? e.message : String(e);
        saveBtn.textContent = "Save";
        saveBtn.disabled = false;  // re-enable only on failure
      }
    });
    bar.appendChild(saveBtn);
    bar.appendChild(err);
    section.appendChild(bar);
    ui.refreshers.push(() => {
      const dirty = ui.controller.anyDirty();
      saveBtn.disabled = !dirty;
      if (dirty && saveBtn.textContent === "Saved") saveBtn.textContent = "Save";
    });
  }
}

interface QuietEntryCtx { ui: EntryUI; weeks: HoursLogWeek[] }

function renderQuietItem(
  item: HoursLogNoActivityItem, entry: QuietEntryCtx | null = null,
): HTMLElement {
  const row = document.createElement("article");
  row.className = "hours-log-item hours-log-item-quiet";

  const title = document.createElement("div");
  title.className = "hours-log-item-title";
  title.textContent = item.name;   // Category is the group header now — no prefix
  // HL-6d: a §B item logs into an in-view week; drop the FROZEN weeks as targets
  // so you can't log new work into a signed-off period. If every in-view week is
  // frozen (e.g. a fully-frozen month scope), show the item read-only (no entry
  // control) — a frozen period freezes everything shown.
  const editableWeeks = entry
    ? entry.weeks.filter((w) => !entry.ui.frozenMonths.has(billingMonth(w.week_end)))
    : [];
  if (entry && editableWeeks.length > 0) {
    row.classList.add("hours-log-item-entry");
    const titleRow = document.createElement("div");
    titleRow.className = "hours-log-item-titlerow";
    titleRow.appendChild(title);
    titleRow.appendChild(makeQuietHoursControl(item, { ui: entry.ui, weeks: editableWeeks }));
    row.appendChild(titleRow);
  } else {
    row.appendChild(title);
  }

  // #40c: a REAL last-touched week (extended lookback), not the unreliable
  // week_end-stamped `last_active`. Shown only when a genuine prior-activity
  // week was found; else the item is dormant beyond the lookback and we say so.
  const meta = document.createElement("div");
  meta.className = "hours-log-quiet-meta muted";
  meta.textContent = item.last_touched
    ? `Last activity: week ending ${String(item.last_touched).slice(0, 10)}`
    : "No activity in the last 16 weeks";
  row.appendChild(meta);

  return row;
}

// §B row control (operator #4): a week dropdown (the weeks in view) + an hours
// cell — the "add this to a week" affordance. The item STAYS here after being
// logged so it can be added to more weeks (operator #1). To avoid the confusing
// double-edit surface (2026-07-23): the dropdown defaults to the next week that
// does NOT yet have this item, so §B always reads as "add to a fresh week" —
// the weeks already logged are edited in their own "Also worked" row. Any edit
// made here still propagates: the blur repaints on ANY value change (not only a
// 0↔positive flip), so the week's placed row never shows a stale number.
function makeQuietHoursControl(
  item: HoursLogNoActivityItem, entry: QuietEntryCtx,
): HTMLElement {
  const { ui, weeks } = entry;
  const rowKey = `item:${item.slug}`;
  const wrap = document.createElement("div");
  wrap.className = "hours-log-quiet-control";

  // Default to the first in-view week WITHOUT hours for this item (the next
  // addable week), else the most-recent (all shown weeks already logged). The
  // caller only builds this control when weeks.length > 0.
  // Default to the first in-view week with NO entry for this item (an explicit
  // 0 counts as logged), else the most-recent.
  const firstUnlogged = weeks.find(
    (w) => !(ui.controller.week(w.week_end)?.hasEntry(rowKey) ?? false),
  );
  let target = (firstUnlogged ?? weeks[0]!).week_end;

  const input = document.createElement("input");
  input.className = "hours-log-cell";
  input.type = "text";
  input.inputMode = "decimal";
  input.setAttribute("aria-label", `Hours for ${item.name}`);
  // A presence+value snapshot at last paint: a blur that changed EITHER (incl.
  // absent → explicit-0, which reads 0 hours both ways) repaints so the week's
  // placed row reflects the edit and §B re-defaults to the next unlogged week.
  const snapshot = (): string => {
    const w = ui.controller.week(target);
    return w?.hasEntry(rowKey) ? `v${w.getHours(rowKey)}` : "∅";
  };
  let painted = snapshot();
  const paintValue = (): void => {
    const w = ui.controller.week(target);
    input.value = w ? cellDisplayValue(w, rowKey) : "";
    painted = snapshot();
  };
  paintValue();
  input.addEventListener("input", () => {
    const w = ui.controller.week(target);
    if (w) commitHoursInput(w, rowKey, item.name, input.value);
    ui.refreshAll();
  });
  input.addEventListener("blur", () => {
    const changed = snapshot() !== painted;
    paintValue();
    // Any change → re-render so the item's placed row under the target week
    // appears / disappears / updates, and §B re-defaults to the next unlogged
    // week (the §B list itself keeps the item for further weeks).
    if (changed) ui.repaint();
  });

  if (weeks.length > 1) {
    const select = document.createElement("select");
    select.className = "hours-log-week-select";
    select.setAttribute("aria-label", `Week for ${item.name}`);
    for (const w of weeks) {
      const o = document.createElement("option");
      o.value = w.week_end;
      o.textContent = fmtWeekRange(w.week_start, w.week_end);
      if (w.week_end === target) o.selected = true;
      select.appendChild(o);
    }
    select.addEventListener("change", () => {
      target = select.value;
      paintValue();  // show the newly-selected week's hours for this item
    });
    wrap.appendChild(select);
  }
  wrap.appendChild(input);
  return wrap;
}
